import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosConfig';
import AddClearanceFileModal from './AddClearanceFileModal';
import EditClearanceFileModal from './EditClearanceFileModal';
import NotesModal from './NotesModal';
import SeaCostModal from './SeaCostModal';
import LandCostModal from './LandCostModal';
import NotificationChoiceModal from './NotificationChoiceModal';
import DateEditModal from './DateEditModal';
import XLSX from 'xlsx-js-style';
import '../styles/nassar-theme.css';
import './ClearanceFiles.css';

// Date input that displays as DD-MM-YYYY but stores value as YYYY-MM-DD internally
const DateInputDDMM = ({ value, onChange, style }) => {
    const pickerRef = useRef(null);

    const isoToDisplay = (iso) => {
        if (!iso) return '';
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
        if (!m) return '';
        return `${m[3]}-${m[2]}-${m[1]}`;
    };

    const displayToIso = (txt) => {
        const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(txt);
        if (!m) return '';
        const dd = parseInt(m[1], 10);
        const mm = parseInt(m[2], 10);
        const yyyy = parseInt(m[3], 10);
        if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1900 || yyyy > 2999) return '';
        return `${m[3]}-${m[2]}-${m[1]}`;
    };

    const [text, setText] = React.useState(isoToDisplay(value));

    React.useEffect(() => {
        setText(isoToDisplay(value));
    }, [value]);

    const handleTextChange = (e) => {
        let raw = e.target.value.replace(/[^\d]/g, '').slice(0, 8);
        let formatted = raw;
        if (raw.length > 4) formatted = `${raw.slice(0, 2)}-${raw.slice(2, 4)}-${raw.slice(4)}`;
        else if (raw.length > 2) formatted = `${raw.slice(0, 2)}-${raw.slice(2)}`;
        setText(formatted);
        const iso = displayToIso(formatted);
        if (iso) onChange(iso);
        else if (!formatted) onChange('');
    };

    const handleBlur = () => {
        const iso = displayToIso(text);
        if (!iso && text) setText(isoToDisplay(value));
    };

    const openPicker = () => {
        const el = pickerRef.current;
        if (!el) return;
        if (typeof el.showPicker === 'function') el.showPicker();
        else el.focus();
    };

    return (
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <input
                type="text"
                value={text}
                onChange={handleTextChange}
                onBlur={handleBlur}
                placeholder="DD-MM-YYYY"
                inputMode="numeric"
                style={{
                    padding: '0.5rem 2rem 0.5rem 0.75rem',
                    border: '1px solid #d5dce4',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    minWidth: '140px',
                    color: '#4a5568',
                    fontFamily: 'inherit',
                    ...style
                }}
            />
            <button
                type="button"
                onClick={openPicker}
                aria-label="open calendar"
                style={{
                    position: 'absolute',
                    right: '6px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#7a8a9e'
                }}
            >
                <svg style={{ width: '16px', height: '16px' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
            </button>
            <input
                ref={pickerRef}
                type="date"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                    pointerEvents: 'none'
                }}
                tabIndex={-1}
            />
        </div>
    );
};

// Module-level cache: keeps search filters alive across remounts (no localStorage)
const searchStateCache = {
    searchQuery: '',
    selectedSearchFields: [],
    fieldSearchValues: {},
    quickCustomerName: '',
    quickStatusName: '',
    expectedArrivalDateFrom: '',
    expectedArrivalDateTo: '',
    inspectionDateFrom: '',
    inspectionDateTo: '',
    showSearchOptions: false,
    currentPage: 1,
};

const ClearanceFiles = () => {
    const [files, setFiles] = useState([]);
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
    const [isLoading, setIsLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedFileForEdit, setSelectedFileForEdit] = useState(null);
    const [error, setError] = useState('');
    const [selectedContainers, setSelectedContainers] = useState({}); // Track selected container per file
    const [notesModalOpen, setNotesModalOpen] = useState(false);
    const [selectedFileNotes, setSelectedFileNotes] = useState(null);
    const [costModalOpen, setCostModalOpen] = useState(false);
    const [selectedFileCost, setSelectedFileCost] = useState(null);
    const [dateEditModal, setDateEditModal] = useState({ isOpen: false, file: null, mode: 'arrival' });
    
    // File statuses for dropdown
    const [fileStatuses, setFileStatuses] = useState([]);
    const [updatingStatusFileId, setUpdatingStatusFileId] = useState(null);
    
    // WhatsApp notification modal state
    const [notificationChoiceModal, setNotificationChoiceModal] = useState({
        isOpen: false,
        notificationType: '',
        notificationData: null
    });

    // Pagination state — currentPage initialized from cache to restore last page on remount
    const [currentPage, setCurrentPage] = useState(searchStateCache.currentPage);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Search state — initialized from module-level cache so values persist on navigation
    const [searchQuery, setSearchQuery] = useState(searchStateCache.searchQuery);
    const [showSearchOptions, setShowSearchOptions] = useState(searchStateCache.showSearchOptions);
    const [selectedSearchFields, setSelectedSearchFields] = useState(searchStateCache.selectedSearchFields);
    const [fieldSearchValues, setFieldSearchValues] = useState(searchStateCache.fieldSearchValues);
    const [quickCustomerName, setQuickCustomerName] = useState(searchStateCache.quickCustomerName);
    const [quickStatusName, setQuickStatusName] = useState(searchStateCache.quickStatusName);

    // Debounce auto-search
    const debounceRef = useRef(null);
    const isFirstRender = useRef(true);

    // Focus restoration after auto-search
    const activeInputRef = useRef(null);
    const fieldInputRefs = useRef({});
    const generalSearchRef = useRef(null);
    const quickCustomerNameRef = useRef(null);
    const quickStatusNameRef = useRef(null);
    const prevLoadingRef = useRef(false);
    
    // Date filters — initialized from module-level cache
    const [expectedArrivalDateFrom, setExpectedArrivalDateFrom] = useState(searchStateCache.expectedArrivalDateFrom);
    const [expectedArrivalDateTo, setExpectedArrivalDateTo] = useState(searchStateCache.expectedArrivalDateTo);
    const [inspectionDateFrom, setInspectionDateFrom] = useState(searchStateCache.inspectionDateFrom);
    const [inspectionDateTo, setInspectionDateTo] = useState(searchStateCache.inspectionDateTo);

    // Available search fields - ordered same as table columns
    const searchFields = React.useMemo(() => [
        { key: 'file_num', label: 'رقم ملف التخليص' },
        { key: 'customer_name', label: 'اسم الزبون' },
        { key: 'goods_description', label: 'وصف البضاعة' },
        { key: 'destination_port_name', label: 'ميناء الوجهة' },
        { key: 'shipment_type_name', label: 'نوع الشحنة' },
        { key: 'container_number', label: 'رقم الحاوية' },
        { key: 'container_type_name', label: 'نوع الحاوية' },
        { key: 'shipment_company_name', label: 'اسم شركة الشحن' },
        { key: 'file_status_name', label: 'الحالة' },
        { key: 'driver_name', label: 'اسم الشفير' },
        { key: 'driver_phonenum', label: 'واتساب الشفير' },
        { key: 'customer_phone', label: 'واتساب الزبون' },
        { key: 'cargo_weight', label: 'وزن البضاعة' },
        { key: 'package_count', label: 'عدد الباكج' },
        { key: 'origin_country', label: 'المنشأ' },
        { key: 'custom_dec_num', label: 'رقم البيان الجمركي' },
        { key: 'authority_name', label: 'اسم المخلص' },
        { key: 'file_type_name', label: 'نوع الملف' },
        { key: 'cost', label: 'تكلفة الملف' },
    ], []);

    // Container-related search fields
    const containerRelatedFields = React.useMemo(() => 
        ['container_number', 'container_type_name', 'cargo_weight', 'package_count'], 
    []);

    const fetchFiles = useCallback(async (page = 1, overrides = null) => {
        setIsLoading(true);
        setError('');
        try {
            const query = overrides !== null ? overrides.searchQuery : searchQuery;
            const fields = overrides !== null ? overrides.selectedSearchFields : selectedSearchFields;
            const fieldValues = overrides !== null ? overrides.fieldSearchValues : fieldSearchValues;
            const customerNameFilter = overrides !== null ? overrides.quickCustomerName : quickCustomerName;
            const statusNameFilter = overrides !== null ? overrides.quickStatusName : quickStatusName;
            const eadFrom = overrides !== null ? overrides.expectedArrivalDateFrom : expectedArrivalDateFrom;
            const eadTo = overrides !== null ? overrides.expectedArrivalDateTo : expectedArrivalDateTo;
            const idFrom = overrides !== null ? overrides.inspectionDateFrom : inspectionDateFrom;
            const idTo = overrides !== null ? overrides.inspectionDateTo : inspectionDateTo;

            let url = `/clearance-files/?page=${page}`;
            
            if (fields.length > 0) {
                fields.forEach(field => {
                    const val = (fieldValues[field] || '').trim();
                    if (val) {
                        url += `&filter_${field}=${encodeURIComponent(val)}`;
                    }
                });
            } else if (query) {
                url += `&search=${encodeURIComponent(query)}`;
                searchFields.forEach(f => {
                    url += `&search_fields=${f.key}`;
                });
            }
            if (customerNameFilter.trim()) {
                url += `&filter_customer_name=${encodeURIComponent(customerNameFilter.trim())}`;
            }
            if (statusNameFilter.trim()) {
                url += `&filter_file_status_name=${encodeURIComponent(statusNameFilter.trim())}`;
            }
            
            let finalEadFrom = eadFrom;
            let finalEadTo = eadTo;
            if (finalEadFrom && finalEadTo && finalEadFrom > finalEadTo) {
                [finalEadFrom, finalEadTo] = [finalEadTo, finalEadFrom];
            }
            let finalIdFrom = idFrom;
            let finalIdTo = idTo;
            if (finalIdFrom && finalIdTo && finalIdFrom > finalIdTo) {
                [finalIdFrom, finalIdTo] = [finalIdTo, finalIdFrom];
            }

            if (finalEadFrom) url += `&expected_arrival_date_from=${finalEadFrom}`;
            if (finalEadTo) url += `&expected_arrival_date_to=${finalEadTo}`;
            if (finalIdFrom) url += `&inspection_date_from=${finalIdFrom}`;
            if (finalIdTo) url += `&inspection_date_to=${finalIdTo}`;

            const response = await axiosInstance.get(url);
            
            if (response.data.results) {
                setFiles(response.data.results);
                setTotalPages(response.data.total_pages);
                setTotalCount(response.data.count);
                setCurrentPage(response.data.current_page);
            } else {
                setFiles(response.data);
                setTotalPages(1);
                setTotalCount(response.data.length);
            }
            
            const initialSelected = {};
            const filesData = response.data.results || response.data;
            const isContainerSearch = fields.some(field => 
                containerRelatedFields.includes(field)
            );
            const hasContainerValues = containerRelatedFields.some(f => (fieldValues[f] || '').trim());
            
            filesData.forEach(file => {
                if (file.containers_data && file.containers_data.length > 0) {
                    if (isContainerSearch && hasContainerValues) {
                        const matchingContainer = file.containers_data.find(container => {
                            const cnVal = (fieldValues['container_number'] || '').trim().toLowerCase();
                            const ctVal = (fieldValues['container_type_name'] || '').trim().toLowerCase();
                            const cwVal = (fieldValues['cargo_weight'] || '').trim();
                            const pcVal = (fieldValues['package_count'] || '').trim();
                            if (cnVal && container.number?.toLowerCase().includes(cnVal)) return true;
                            if (ctVal && container.container_type_name?.toLowerCase().includes(ctVal)) return true;
                            if (cwVal && String(container.cargo_weight).includes(cwVal)) return true;
                            if (pcVal && String(container.package_count).includes(pcVal)) return true;
                            return false;
                        });
                        initialSelected[file.id] = matchingContainer ? matchingContainer.id : file.containers_data[0].id;
                    } else if (query) {
                        const q = query.trim().toLowerCase();
                        const matchingContainer = file.containers_data.find(container => {
                            if (container.number?.toLowerCase().includes(q)) return true;
                            if (container.container_type_name?.toLowerCase().includes(q)) return true;
                            if (String(container.cargo_weight).toLowerCase().includes(q)) return true;
                            if (String(container.package_count).toLowerCase().includes(q)) return true;
                            return false;
                        });
                        initialSelected[file.id] = matchingContainer ? matchingContainer.id : file.containers_data[0].id;
                    } else {
                        initialSelected[file.id] = file.containers_data[0].id;
                    }
                }
            });
            setSelectedContainers(prev => ({...prev, ...initialSelected}));
        } catch (err) {
            setError('حدث خطأ أثناء جلب البيانات');
            console.error('Error fetching files:', err);
        } finally {
            setIsLoading(false);
        }
    }, [searchQuery, selectedSearchFields, fieldSearchValues, quickCustomerName, quickStatusName, expectedArrivalDateFrom, expectedArrivalDateTo, inspectionDateFrom, inspectionDateTo, containerRelatedFields, searchFields]);

    useEffect(() => {
        fetchFiles(searchStateCache.currentPage || 1);
        // Fetch file statuses for dropdown
        axiosInstance.get('/file-statuses/')
            .then(res => setFileStatuses(res.data))
            .catch(err => console.error('Error fetching file statuses:', err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist current search state into the module-level cache on every change
    useEffect(() => {
        searchStateCache.searchQuery = searchQuery;
        searchStateCache.selectedSearchFields = selectedSearchFields;
        searchStateCache.fieldSearchValues = fieldSearchValues;
        searchStateCache.quickCustomerName = quickCustomerName;
        searchStateCache.quickStatusName = quickStatusName;
        searchStateCache.expectedArrivalDateFrom = expectedArrivalDateFrom;
        searchStateCache.expectedArrivalDateTo = expectedArrivalDateTo;
        searchStateCache.inspectionDateFrom = inspectionDateFrom;
        searchStateCache.inspectionDateTo = inspectionDateTo;
        searchStateCache.showSearchOptions = showSearchOptions;
        searchStateCache.currentPage = currentPage;
    }, [
        searchQuery,
        selectedSearchFields,
        fieldSearchValues,
        quickCustomerName,
        quickStatusName,
        expectedArrivalDateFrom,
        expectedArrivalDateTo,
        inspectionDateFrom,
        inspectionDateTo,
        showSearchOptions,
        currentPage,
    ]);

    // Auto-search on typing (debounced)
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setCurrentPage(1);
            fetchFiles(1, {
                searchQuery,
                selectedSearchFields,
                fieldSearchValues,
                quickCustomerName,
                quickStatusName,
                expectedArrivalDateFrom,
                expectedArrivalDateTo,
                inspectionDateFrom,
                inspectionDateTo
            });
        }, 400);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, fieldSearchValues, quickCustomerName, quickStatusName]);

    useEffect(() => {
        if (prevLoadingRef.current && !isLoading && activeInputRef.current) {
            requestAnimationFrame(() => {
                if (activeInputRef.current === '__general__' && generalSearchRef.current) {
                    generalSearchRef.current.focus();
                } else if (activeInputRef.current === '__quick_customer__' && quickCustomerNameRef.current) {
                    quickCustomerNameRef.current.focus();
                } else if (activeInputRef.current === '__quick_status__' && quickStatusNameRef.current) {
                    quickStatusNameRef.current.focus();
                } else if (fieldInputRefs.current[activeInputRef.current]) {
                    fieldInputRefs.current[activeInputRef.current].focus();
                }
            });
        }
        prevLoadingRef.current = isLoading;
    }, [isLoading]);

    const highlightText = (text, fieldKey) => {
        if (!text || text === '-') return text;
        const textStr = String(text);
        let searchTerm = '';
        if (selectedSearchFields.length > 0 && fieldKey) {
            searchTerm = (fieldSearchValues[fieldKey] || '').trim();
        } else if (selectedSearchFields.length === 0) {
            if (fieldKey === 'customer_name') {
                searchTerm = quickCustomerName.trim() || searchQuery.trim();
            } else if (fieldKey === 'file_status_name') {
                searchTerm = quickStatusName.trim() || searchQuery.trim();
            } else {
                searchTerm = searchQuery.trim();
            }
        }
        if (!searchTerm) return textStr;
        try {
            const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(${escaped})`, 'gi');
            const parts = textStr.split(regex);
            if (parts.length <= 1) return textStr;
            return parts.map((part, i) =>
                i % 2 === 1 ? <span key={i} style={{ backgroundColor: '#fff176', padding: '0 1px', borderRadius: '2px' }}>{part}</span> : part
            );
        } catch { return textStr; }
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setCurrentPage(1);
        fetchFiles(1, {
            searchQuery,
            selectedSearchFields,
            fieldSearchValues,
            quickCustomerName,
            quickStatusName,
            expectedArrivalDateFrom,
            expectedArrivalDateTo,
            inspectionDateFrom,
            inspectionDateTo
        });
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setSelectedSearchFields([]);
        setFieldSearchValues({});
        setQuickCustomerName('');
        setQuickStatusName('');
        setExpectedArrivalDateFrom('');
        setExpectedArrivalDateTo('');
        setInspectionDateFrom('');
        setInspectionDateTo('');
        setCurrentPage(1);
        fetchFiles(1, {
            searchQuery: '',
            selectedSearchFields: [],
            fieldSearchValues: {},
            quickCustomerName: '',
            quickStatusName: '',
            expectedArrivalDateFrom: '',
            expectedArrivalDateTo: '',
            inspectionDateFrom: '',
            inspectionDateTo: ''
        });
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            fetchFiles(newPage);
        }
    };

    const toggleSearchField = (fieldKey) => {
        setSelectedSearchFields(prev => {
            if (prev.includes(fieldKey)) {
                setFieldSearchValues(fv => {
                    const updated = { ...fv };
                    delete updated[fieldKey];
                    return updated;
                });
                const newFields = prev.filter(f => f !== fieldKey);
                if (newFields.length === 0) {
                    setSearchQuery('');
                }
                return newFields;
            }
            setSearchQuery('');
            return [...prev, fieldKey];
        });
    };

    const handleAddSuccess = () => {
        fetchFiles(currentPage);
    };

    const handleEditClick = async (file) => {
        try {
            // Fetch full file data including all IDs
            const response = await axiosInstance.get(`/clearance-files/${file.id}/`);
            setSelectedFileForEdit(response.data);
            setIsEditModalOpen(true);
        } catch (err) {
            console.error('Error fetching file details:', err);
            setError('حدث خطأ أثناء جلب بيانات الملف');
        }
    };

    const handleEditSuccess = () => {
        fetchFiles(currentPage);
    };

    const handleContainerChange = (fileId, containerId) => {
        setSelectedContainers(prev => ({
            ...prev,
            [fileId]: parseInt(containerId)
        }));
    };

    const getSelectedContainer = (file) => {
        if (!file.containers_data || file.containers_data.length === 0) {
            return null;
        }
        const selectedId = selectedContainers[file.id];
        return file.containers_data.find(c => c.id === selectedId) || file.containers_data[0];
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB');
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    const formatTime = (timeString) => {
        if (!timeString) return '-';
        return timeString;
    };

    const handleNotesClick = (file) => {
        setSelectedFileNotes({
            fileId: file.id,
            fileNum: file.file_num,
            customerName: file.customer_name || '',
            goodsDescription: file.goods_description || '',
            containerNumbers: file.containers_data?.map(c => c.number).filter(n => n) || [],
            notes: file.all_notes || []
        });
        setNotesModalOpen(true);
    };

    const handleCostClick = (file) => {
        const fileTypeName = (file.file_type_name || '').toUpperCase();
        let modalType = '';
        
        // Determine modal type based on file type
        if (fileTypeName.includes('SEA') || fileTypeName.includes('بحري') || fileTypeName.includes('MARITIME') || fileTypeName.includes('بحر')) {
            modalType = 'SEA';
        } else if (fileTypeName.includes('AIR') || fileTypeName.includes('جوي') || fileTypeName.includes('جو')) {
            modalType = 'AIR';
        } else if (fileTypeName.includes('LAND') || fileTypeName.includes('بري') || fileTypeName.includes('أرضي') || fileTypeName.includes('بر')) {
            modalType = 'LAND';
        }
        
        setSelectedFileCost({
            fileId: file.id,
            fileNum: file.file_num,
            cost: file.cost,
            modalType: modalType
        });
        setCostModalOpen(true);
    };

    const openWhatsApp = (phoneNumber) => {
        if (!phoneNumber) return;
        // Remove any non-digit characters
        const cleanNumber = phoneNumber.replace(/\D/g, '');
        window.open(`https://wa.me/${cleanNumber}`, '_blank');
    };

    // Handle status change from dropdown
    // Helper function to check if status is Gate Pass
    const isGatePassStatus = (statusId) => {
        const status = fileStatuses.find(s => s.id === parseInt(statusId));
        if (!status) return false;
        const name = (status.name || '').toLowerCase();
        return name.includes('جيت') || name.includes('gate') || name.includes('pass');
    };

    const handleStatusChange = async (fileId, newStatusId) => {
        if (!newStatusId) return;
        
        const file = files.find(f => f.id === fileId);
        const wasGatePass = file ? isGatePassStatus(file.file_status) : false;
        const isNowGatePass = isGatePassStatus(newStatusId);
        const statusChangedToGatePass = !wasGatePass && isNowGatePass;
        
        setUpdatingStatusFileId(fileId);
        try {
            const response = await axiosInstance.patch(`/clearance-files/${fileId}/`, {
                file_status: parseInt(newStatusId)
            });
            const savedFile = response.data;
            
            // Update local state to reflect the change
            const newStatus = fileStatuses.find(s => s.id === parseInt(newStatusId));
            setFiles(prevFiles => prevFiles.map(f => {
                if (f.id === fileId) {
                    return {
                        ...f,
                        file_status: parseInt(newStatusId),
                        file_status_name: newStatus?.name || f.file_status_name
                    };
                }
                return f;
            }));
            
            // Show Gate Pass notification if status changed to Gate Pass
            // Use savedFile from API response for accurate data
            if (statusChangedToGatePass && savedFile?.customer_phone) {
                const containerNumbers = savedFile.containers_data?.map(c => c.number).filter(n => n) || [];
                const totalPackageCount = savedFile.containers_data?.reduce((sum, c) => {
                    return sum + (parseInt(c.package_count) || 0);
                }, 0) || 0;
                
                setNotificationChoiceModal({
                    isOpen: true,
                    notificationType: 'gate-pass',
                    notificationData: {
                        customer_phone: savedFile.customer_phone,
                        file_num: savedFile.file_num || '',
                        container_numbers: containerNumbers,
                        custom_dec_num: savedFile.custom_dec_num || '',
                        package_count: totalPackageCount || '',
                        goods_description: savedFile.goods_description || ''
                    }
                });
            }
        } catch (err) {
            console.error('Error updating file status:', err);
            setError('حدث خطأ أثناء تحديث الحالة');
        } finally {
            setUpdatingStatusFileId(null);
        }
    };

    // Get status badge color based on status name
    const getStatusColor = (statusName) => {
        if (!statusName) return { bg: '#6c757d', color: '#fff' }; // Default gray
        
        const normalizedStatus = statusName.trim().toLowerCase();
        
        // قيد التخليص - Dark Blue
        if (normalizedStatus.includes('قيد التخليص') || normalizedStatus.includes('قيد') || normalizedStatus.includes('تخليص')) {
            return { bg: '#0277bd', color: '#fff' };
        }
        
        // عالق جمارك - Red
        if (normalizedStatus.includes('عالق') || normalizedStatus.includes('جمارك')) {
            return { bg: '#dc3545', color: '#fff' };
        }
        
        // جيت باس - Green
        if (normalizedStatus.includes('جيت') || normalizedStatus.includes('باس') || normalizedStatus.includes('gate') || normalizedStatus.includes('pass')) {
            return { bg: '#28a745', color: '#fff' };
        }
        
        // Default for other statuses - Dark gray/black
        return { bg: '#495057', color: '#fff' };
    };

    // Pagination component
    const Pagination = () => (
        <div className="pagination-container" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1.5rem',
            padding: '1rem',
            flexWrap: 'wrap'
        }}>
            <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="pagination-btn"
                style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                    color: currentPage === 1 ? '#aaa' : '#03428d',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
            >
                الأولى
            </button>
            <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
                style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                    color: currentPage === 1 ? '#aaa' : '#03428d',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
            >
                السابق
            </button>
            
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#03428d',
                color: '#fff',
                borderRadius: '8px',
                fontWeight: '600'
            }}>
                <span>صفحة {currentPage} من {totalPages}</span>
                <span style={{ opacity: 0.8, fontSize: '0.85rem' }}>({totalCount} نتيجة)</span>
            </div>
            
            <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
                style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                    color: currentPage === totalPages ? '#aaa' : '#03428d',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
            >
                التالي
            </button>
            <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
                style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                    color: currentPage === totalPages ? '#aaa' : '#03428d',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
            >
                الأخيرة
            </button>
        </div>
    );

    // Export to Excel
    const [isExporting, setIsExporting] = useState(false);

    // Export choice & selection modal state
    const [exportChoiceModalOpen, setExportChoiceModalOpen] = useState(false);
    const [exportSelectModalOpen, setExportSelectModalOpen] = useState(false);
    const [selectExportFiles, setSelectExportFiles] = useState([]);
    const [selectExportPage, setSelectExportPage] = useState(1);
    const [selectExportTotalPages, setSelectExportTotalPages] = useState(1);
    const [selectExportTotalCount, setSelectExportTotalCount] = useState(0);
    const [selectedExportIds, setSelectedExportIds] = useState(() => new Set());
    const [isLoadingSelectExport, setIsLoadingSelectExport] = useState(false);
    const selectExportDebounceRef = useRef(null);

    // Modal-specific search/filter state (mirrors the main page state)
    const [mSearchQuery, setMSearchQuery] = useState('');
    const [mShowSearchOptions, setMShowSearchOptions] = useState(false);
    const [mSelectedSearchFields, setMSelectedSearchFields] = useState([]);
    const [mFieldSearchValues, setMFieldSearchValues] = useState({});
    const [mQuickCustomerName, setMQuickCustomerName] = useState('');
    const [mQuickStatusName, setMQuickStatusName] = useState('');
    const [mExpectedArrivalDateFrom, setMExpectedArrivalDateFrom] = useState('');
    const [mExpectedArrivalDateTo, setMExpectedArrivalDateTo] = useState('');
    const [mInspectionDateFrom, setMInspectionDateFrom] = useState('');
    const [mInspectionDateTo, setMInspectionDateTo] = useState('');
    const [mSelectedContainers, setMSelectedContainers] = useState({});
    const mGeneralSearchRef = useRef(null);
    const mQuickCustomerNameRef = useRef(null);
    const mQuickStatusNameRef = useRef(null);
    const mFieldInputRefs = useRef({});
    const mActiveInputRef = useRef(null);
    const mPrevLoadingRef = useRef(false);

    // Build and download an Excel file from a given list of files
    const buildAndDownloadExcel = (allFiles) => {
        const rows = [];
        allFiles.forEach(file => {
            const containers = file.containers_data && file.containers_data.length > 0
                ? file.containers_data
                : [null];

            containers.forEach(container => {
                rows.push({
                    'رقم ملف التخليص': file.file_num || '',
                    'اسم الزبون': file.customer_name || '',
                    'وصف البضاعة': file.goods_description || '',
                    'ميناء الوجهة': file.destination_port_name || '',
                    'نوع الشحنة': file.shipment_type_name || '',
                    'رقم الحاوية': container ? container.number || '' : '',
                    'نوع الحاوية': container ? container.container_type_name || '' : '',
                    'وزن البضاعة': container ? container.cargo_weight || '' : '',
                    'عدد الباكج': container ? container.package_count || '' : '',
                    'اسم شركة الشحن': file.shipment_company_name || '',
                    'وصول متوقع': file.expected_arrival_date ? new Date(file.expected_arrival_date).toLocaleDateString('en-GB') : '',
                    'موعد الفحص': file.inspection_date ? new Date(file.inspection_date).toLocaleDateString('en-GB') : '',
                    'وقت الفحص': file.inspection_time || '',
                    'الحالة': file.file_status_name || '',
                    'اسم الشفير': file.driver_name || '',
                    'واتساب الشفير': file.driver_phonenum || '',
                    'واتساب الزبون': file.customer_phone || '',
                    'المنشأ': file.origin_country_name || '',
                    'رقم البيان الجمركي': file.custom_dec_num || '',
                    'اسم المخلص': file.authority_name || '',
                    'نوع الملف': file.file_type_name || '',
                    'التكلفة': file.cost ? Math.floor(parseFloat(file.cost)) : 0,
                    'تاريخ انشاء الملف': file.created_at ? new Date(file.created_at).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : ''
                });
            });
        });

        if (rows.length === 0) {
            setError('لا توجد بيانات للتصدير');
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(rows);
        worksheet['!rtl'] = true;

        const headerKeys = Object.keys(rows[0] || {});
        const headerStyle = {
            fill: { fgColor: { rgb: '217346' } },
            font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
            alignment: { horizontal: 'center', vertical: 'center', readingOrder: 2 }
        };
        const cellStyle = {
            alignment: { horizontal: 'center', vertical: 'center', readingOrder: 2 }
        };
        headerKeys.forEach((key, colIdx) => {
            const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIdx });
            if (worksheet[cellRef]) {
                worksheet[cellRef].s = headerStyle;
            }
        });

        const range = XLSX.utils.decode_range(worksheet['!ref']);
        for (let r = 1; r <= range.e.r; r++) {
            for (let c = 0; c <= range.e.c; c++) {
                const cellRef = XLSX.utils.encode_cell({ r, c });
                if (worksheet[cellRef]) {
                    worksheet[cellRef].s = cellStyle;
                }
            }
        }

        const colWidths = headerKeys.map(key => ({
            wch: Math.max(key.length * 2, ...rows.map(r => String(r[key] || '').length)) + 2
        }));
        worksheet['!cols'] = colWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'ملفات التخليص');

        workbook.Workbook = workbook.Workbook || {};
        workbook.Workbook.Views = [{ RTL: true }];

        const now = new Date();
        const dateStr = now.toLocaleDateString('en-GB').replace(/\//g, '-');
        const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).replace(/:/g, '-');
        const fileName = `ملفات_التخليص_${dateStr}_${timeStr}.xlsx`;

        XLSX.writeFile(workbook, fileName);
    };

    const exportToExcel = async () => {
        setIsExporting(true);
        try {
            let url = '/clearance-files/';
            const params = [];
            if (selectedSearchFields.length > 0) {
                selectedSearchFields.forEach(field => {
                    const val = (fieldSearchValues[field] || '').trim();
                    if (val) {
                        params.push(`filter_${field}=${encodeURIComponent(val)}`);
                    }
                });
            } else if (searchQuery) {
                params.push(`search=${encodeURIComponent(searchQuery)}`);
                searchFields.forEach(f => {
                    params.push(`search_fields=${f.key}`);
                });
            }
            let expFrom = expectedArrivalDateFrom;
            let expTo = expectedArrivalDateTo;
            if (expFrom && expTo && expFrom > expTo) {
                [expFrom, expTo] = [expTo, expFrom];
            }
            let insFrom = inspectionDateFrom;
            let insTo = inspectionDateTo;
            if (insFrom && insTo && insFrom > insTo) {
                [insFrom, insTo] = [insTo, insFrom];
            }
            if (expFrom) params.push(`expected_arrival_date_from=${expFrom}`);
            if (expTo) params.push(`expected_arrival_date_to=${expTo}`);
            if (insFrom) params.push(`inspection_date_from=${insFrom}`);
            if (insTo) params.push(`inspection_date_to=${insTo}`);
            if (params.length > 0) url += '?' + params.join('&');

            const response = await axiosInstance.get(url);
            const allFiles = Array.isArray(response.data) ? response.data : (response.data.results || []);
            buildAndDownloadExcel(allFiles);
        } catch (err) {
            console.error('Error exporting to Excel:', err);
            setError('حدث خطأ أثناء التصدير إلى Excel');
        } finally {
            setIsExporting(false);
        }
    };

    // Fetch files for the selection modal with full search/filter parity with fetchFiles
    const fetchModalFiles = useCallback(async (page = 1, overrides = null) => {
        setIsLoadingSelectExport(true);
        try {
            const query = overrides !== null ? overrides.searchQuery : mSearchQuery;
            const fields = overrides !== null ? overrides.selectedSearchFields : mSelectedSearchFields;
            const fieldValues = overrides !== null ? overrides.fieldSearchValues : mFieldSearchValues;
            const customerNameFilter = overrides !== null ? overrides.quickCustomerName : mQuickCustomerName;
            const statusNameFilter = overrides !== null ? overrides.quickStatusName : mQuickStatusName;
            const eadFrom = overrides !== null ? overrides.expectedArrivalDateFrom : mExpectedArrivalDateFrom;
            const eadTo = overrides !== null ? overrides.expectedArrivalDateTo : mExpectedArrivalDateTo;
            const idFrom = overrides !== null ? overrides.inspectionDateFrom : mInspectionDateFrom;
            const idTo = overrides !== null ? overrides.inspectionDateTo : mInspectionDateTo;

            let url = `/clearance-files/?page=${page}`;

            if (fields.length > 0) {
                fields.forEach(field => {
                    const val = (fieldValues[field] || '').trim();
                    if (val) {
                        url += `&filter_${field}=${encodeURIComponent(val)}`;
                    }
                });
            } else if (query) {
                url += `&search=${encodeURIComponent(query)}`;
                searchFields.forEach(f => {
                    url += `&search_fields=${f.key}`;
                });
            }
            if (customerNameFilter.trim()) {
                url += `&filter_customer_name=${encodeURIComponent(customerNameFilter.trim())}`;
            }
            if (statusNameFilter.trim()) {
                url += `&filter_file_status_name=${encodeURIComponent(statusNameFilter.trim())}`;
            }

            let finalEadFrom = eadFrom;
            let finalEadTo = eadTo;
            if (finalEadFrom && finalEadTo && finalEadFrom > finalEadTo) {
                [finalEadFrom, finalEadTo] = [finalEadTo, finalEadFrom];
            }
            let finalIdFrom = idFrom;
            let finalIdTo = idTo;
            if (finalIdFrom && finalIdTo && finalIdFrom > finalIdTo) {
                [finalIdFrom, finalIdTo] = [finalIdTo, finalIdFrom];
            }

            if (finalEadFrom) url += `&expected_arrival_date_from=${finalEadFrom}`;
            if (finalEadTo) url += `&expected_arrival_date_to=${finalEadTo}`;
            if (finalIdFrom) url += `&inspection_date_from=${finalIdFrom}`;
            if (finalIdTo) url += `&inspection_date_to=${finalIdTo}`;

            const response = await axiosInstance.get(url);

            let filesData;
            if (response.data.results) {
                setSelectExportFiles(response.data.results);
                setSelectExportTotalPages(response.data.total_pages);
                setSelectExportTotalCount(response.data.count);
                setSelectExportPage(response.data.current_page);
                filesData = response.data.results;
            } else {
                setSelectExportFiles(response.data);
                setSelectExportTotalPages(1);
                setSelectExportTotalCount(response.data.length);
                setSelectExportPage(1);
                filesData = response.data;
            }

            const initialSelected = {};
            const isContainerSearch = fields.some(field => containerRelatedFields.includes(field));
            const hasContainerValues = containerRelatedFields.some(f => (fieldValues[f] || '').trim());

            filesData.forEach(file => {
                if (file.containers_data && file.containers_data.length > 0) {
                    if (isContainerSearch && hasContainerValues) {
                        const matchingContainer = file.containers_data.find(container => {
                            const cnVal = (fieldValues['container_number'] || '').trim().toLowerCase();
                            const ctVal = (fieldValues['container_type_name'] || '').trim().toLowerCase();
                            const cwVal = (fieldValues['cargo_weight'] || '').trim();
                            const pcVal = (fieldValues['package_count'] || '').trim();
                            if (cnVal && container.number?.toLowerCase().includes(cnVal)) return true;
                            if (ctVal && container.container_type_name?.toLowerCase().includes(ctVal)) return true;
                            if (cwVal && String(container.cargo_weight).includes(cwVal)) return true;
                            if (pcVal && String(container.package_count).includes(pcVal)) return true;
                            return false;
                        });
                        initialSelected[file.id] = matchingContainer ? matchingContainer.id : file.containers_data[0].id;
                    } else if (query) {
                        const q = query.trim().toLowerCase();
                        const matchingContainer = file.containers_data.find(container => {
                            if (container.number?.toLowerCase().includes(q)) return true;
                            if (container.container_type_name?.toLowerCase().includes(q)) return true;
                            if (String(container.cargo_weight).toLowerCase().includes(q)) return true;
                            if (String(container.package_count).toLowerCase().includes(q)) return true;
                            return false;
                        });
                        initialSelected[file.id] = matchingContainer ? matchingContainer.id : file.containers_data[0].id;
                    } else {
                        initialSelected[file.id] = file.containers_data[0].id;
                    }
                }
            });
            setMSelectedContainers(prev => ({ ...prev, ...initialSelected }));
        } catch (err) {
            console.error('Error fetching files for selection modal:', err);
        } finally {
            setIsLoadingSelectExport(false);
        }
    }, [mSearchQuery, mSelectedSearchFields, mFieldSearchValues, mQuickCustomerName, mQuickStatusName,
        mExpectedArrivalDateFrom, mExpectedArrivalDateTo, mInspectionDateFrom, mInspectionDateTo,
        searchFields, containerRelatedFields]);

    const openExportSelectModal = () => {
        setExportChoiceModalOpen(false);
        setMSearchQuery('');
        setMShowSearchOptions(false);
        setMSelectedSearchFields([]);
        setMFieldSearchValues({});
        setMQuickCustomerName('');
        setMQuickStatusName('');
        setMExpectedArrivalDateFrom('');
        setMExpectedArrivalDateTo('');
        setMInspectionDateFrom('');
        setMInspectionDateTo('');
        setSelectedExportIds(new Set());
        setSelectExportPage(1);
        setExportSelectModalOpen(true);
        fetchModalFiles(1, {
            searchQuery: '', selectedSearchFields: [], fieldSearchValues: {},
            quickCustomerName: '', quickStatusName: '',
            expectedArrivalDateFrom: '', expectedArrivalDateTo: '',
            inspectionDateFrom: '', inspectionDateTo: ''
        });
    };

    const handleModalSearch = (e) => {
        if (e) e.preventDefault();
        setSelectExportPage(1);
        fetchModalFiles(1, {
            searchQuery: mSearchQuery,
            selectedSearchFields: mSelectedSearchFields,
            fieldSearchValues: mFieldSearchValues,
            quickCustomerName: mQuickCustomerName,
            quickStatusName: mQuickStatusName,
            expectedArrivalDateFrom: mExpectedArrivalDateFrom,
            expectedArrivalDateTo: mExpectedArrivalDateTo,
            inspectionDateFrom: mInspectionDateFrom,
            inspectionDateTo: mInspectionDateTo
        });
    };

    const handleModalClearSearch = () => {
        setMSearchQuery('');
        setMSelectedSearchFields([]);
        setMFieldSearchValues({});
        setMQuickCustomerName('');
        setMQuickStatusName('');
        setMExpectedArrivalDateFrom('');
        setMExpectedArrivalDateTo('');
        setMInspectionDateFrom('');
        setMInspectionDateTo('');
        setSelectExportPage(1);
        fetchModalFiles(1, {
            searchQuery: '', selectedSearchFields: [], fieldSearchValues: {},
            quickCustomerName: '', quickStatusName: '',
            expectedArrivalDateFrom: '', expectedArrivalDateTo: '',
            inspectionDateFrom: '', inspectionDateTo: ''
        });
    };

    const handleModalPageChange = (newPage) => {
        if (newPage >= 1 && newPage <= selectExportTotalPages) {
            setSelectExportPage(newPage);
            fetchModalFiles(newPage);
        }
    };

    const toggleModalSearchField = (fieldKey) => {
        setMSelectedSearchFields(prev => {
            if (prev.includes(fieldKey)) {
                setMFieldSearchValues(fv => {
                    const updated = { ...fv };
                    delete updated[fieldKey];
                    return updated;
                });
                const newFields = prev.filter(f => f !== fieldKey);
                if (newFields.length === 0) {
                    setMSearchQuery('');
                }
                return newFields;
            }
            setMSearchQuery('');
            return [...prev, fieldKey];
        });
    };

    const handleModalContainerChange = (fileId, containerId) => {
        setMSelectedContainers(prev => ({
            ...prev,
            [fileId]: parseInt(containerId)
        }));
    };

    const getModalSelectedContainer = (file) => {
        if (!file.containers_data || file.containers_data.length === 0) {
            return null;
        }
        const selectedId = mSelectedContainers[file.id];
        return file.containers_data.find(c => c.id === selectedId) || file.containers_data[0];
    };

    const highlightModalText = (text, fieldKey) => {
        if (!text || text === '-') return text;
        const textStr = String(text);
        let searchTerm = '';
        if (mSelectedSearchFields.length > 0 && fieldKey) {
            searchTerm = (mFieldSearchValues[fieldKey] || '').trim();
        } else if (mSelectedSearchFields.length === 0) {
            if (fieldKey === 'customer_name') {
                searchTerm = mQuickCustomerName.trim() || mSearchQuery.trim();
            } else if (fieldKey === 'file_status_name') {
                searchTerm = mQuickStatusName.trim() || mSearchQuery.trim();
            } else {
                searchTerm = mSearchQuery.trim();
            }
        }
        if (!searchTerm) return textStr;
        try {
            const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(${escaped})`, 'gi');
            const parts = textStr.split(regex);
            if (parts.length <= 1) return textStr;
            return parts.map((part, i) =>
                i % 2 === 1 ? <span key={i} style={{ backgroundColor: '#fff176', padding: '0 1px', borderRadius: '2px' }}>{part}</span> : part
            );
        } catch { return textStr; }
    };

    const toggleExportFileSelection = (fileId) => {
        setSelectedExportIds(prev => {
            const next = new Set(prev);
            if (next.has(fileId)) next.delete(fileId);
            else next.add(fileId);
            return next;
        });
    };

    const allCurrentPageSelected = selectExportFiles.length > 0 &&
        selectExportFiles.every(f => selectedExportIds.has(f.id));

    const toggleSelectAllCurrentPage = () => {
        setSelectedExportIds(prev => {
            const next = new Set(prev);
            if (allCurrentPageSelected) {
                selectExportFiles.forEach(f => next.delete(f.id));
            } else {
                selectExportFiles.forEach(f => next.add(f.id));
            }
            return next;
        });
    };

    const exportSelectedFiles = async () => {
        if (selectedExportIds.size === 0) {
            setError('لم يتم اختيار أي ملف للتصدير');
            return;
        }
        setIsExporting(true);
        setExportSelectModalOpen(false);
        try {
            const res = await axiosInstance.get('/clearance-files/');
            const all = Array.isArray(res.data) ? res.data : (res.data.results || []);
            const filtered = all.filter(f => selectedExportIds.has(f.id));
            if (filtered.length === 0) {
                setError('لم يتم العثور على ملفات للتصدير');
                return;
            }
            buildAndDownloadExcel(filtered);
            setSelectedExportIds(new Set());
        } catch (err) {
            console.error('Error exporting selected to Excel:', err);
            setError('حدث خطأ أثناء التصدير إلى Excel');
        } finally {
            setIsExporting(false);
        }
    };

    // Auto-search debounce inside the selection modal
    useEffect(() => {
        if (!exportSelectModalOpen) return;
        if (selectExportDebounceRef.current) clearTimeout(selectExportDebounceRef.current);
        selectExportDebounceRef.current = setTimeout(() => {
            setSelectExportPage(1);
            fetchModalFiles(1, {
                searchQuery: mSearchQuery,
                selectedSearchFields: mSelectedSearchFields,
                fieldSearchValues: mFieldSearchValues,
                quickCustomerName: mQuickCustomerName,
                quickStatusName: mQuickStatusName,
                expectedArrivalDateFrom: mExpectedArrivalDateFrom,
                expectedArrivalDateTo: mExpectedArrivalDateTo,
                inspectionDateFrom: mInspectionDateFrom,
                inspectionDateTo: mInspectionDateTo
            });
        }, 400);
        return () => { if (selectExportDebounceRef.current) clearTimeout(selectExportDebounceRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mSearchQuery, mFieldSearchValues, mQuickCustomerName, mQuickStatusName, exportSelectModalOpen]);

    // Restore focus to active input after modal loading completes
    useEffect(() => {
        if (mPrevLoadingRef.current && !isLoadingSelectExport && mActiveInputRef.current) {
            requestAnimationFrame(() => {
                if (mActiveInputRef.current === '__general__' && mGeneralSearchRef.current) {
                    mGeneralSearchRef.current.focus();
                } else if (mActiveInputRef.current === '__quick_customer__' && mQuickCustomerNameRef.current) {
                    mQuickCustomerNameRef.current.focus();
                } else if (mActiveInputRef.current === '__quick_status__' && mQuickStatusNameRef.current) {
                    mQuickStatusNameRef.current.focus();
                } else if (mFieldInputRefs.current[mActiveInputRef.current]) {
                    mFieldInputRefs.current[mActiveInputRef.current].focus();
                }
            });
        }
        mPrevLoadingRef.current = isLoadingSelectExport;
    }, [isLoadingSelectExport]);

    if (isLoading && files.length === 0 && !isAddModalOpen && !isEditModalOpen) {
        return (
            <div className="loading-container">
                <div className="spinner" style={{ width: '45px', height: '45px' }}></div>
                <p style={{ marginTop: '0.75rem', color: '#6c757d', fontSize: '1.1rem' }}>جاري التحميل...</p>
            </div>
        );
    }

    return (
        <div className="clearance-files-page">
            {/* Header Actions */}
            <div className="page-header">
                <div className="header-left" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <button 
                        className="btn btn-accent btn-large"
                        onClick={() => setIsAddModalOpen(true)}
                    >
                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        <span>إضافة ملف تخليص</span>
                    </button>
                    <button 
                        className="btn btn-large"
                        onClick={() => setExportChoiceModalOpen(true)}
                        disabled={isExporting}
                        style={{
                            background: 'linear-gradient(135deg, #217346 0%, #2e8b57 100%)',
                            color: 'white',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.6rem 1.25rem',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: isExporting ? 'not-allowed' : 'pointer',
                            opacity: isExporting ? 0.7 : 1,
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {isExporting ? (
                            <>
                                <div className="spinner" style={{ width: '18px', height: '18px' }}></div>
                                <span>جاري التصدير...</span>
                            </>
                        ) : (
                            <>
                                <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                <span>تصدير Excel</span>
                            </>
                        )}
                    </button>
                </div>

                <div className="view-toggle">
                    <button 
                        className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                        onClick={() => setViewMode('table')}
                    >
                        <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                        </svg>
                        <span>جدول</span>
                    </button>
                    <button 
                        className={`toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
                        onClick={() => setViewMode('card')}
                    >
                        <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                        </svg>
                        <span>كروت</span>
                    </button>
                </div>
            </div>

            {/* Search Section */}
            <div className="search-section" style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e8edf2'
            }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {/* Search Input */}
                    <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
                        <input
                            ref={generalSearchRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                            placeholder="ادخل نص البحث..."
                            disabled={selectedSearchFields.length > 0}
                            style={{
                                width: '100%',
                                padding: '0.75rem 1rem',
                                border: '1.5px solid #d5dce4',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                transition: 'all 0.2s ease',
                                outline: 'none',
                                color: selectedSearchFields.length > 0 ? '#a0aec0' : '#4a5568',
                                backgroundColor: selectedSearchFields.length > 0 ? '#f0f2f5' : '#fff',
                                cursor: selectedSearchFields.length > 0 ? 'not-allowed' : 'text'
                            }}
                            onFocus={(e) => { activeInputRef.current = '__general__'; if (selectedSearchFields.length === 0) e.target.style.borderColor = '#5b8abf'; }}
                            onBlur={(e) => e.target.style.borderColor = '#d5dce4'}
                        />
                    </div>
                    
                    {/* Search By Button */}
                    <button
                        onClick={() => setShowSearchOptions(!showSearchOptions)}
                        style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: showSearchOptions ? '#03428d' : '#f0f4f8',
                            color: showSearchOptions ? '#fff' : '#5a6f85',
                            border: '1.5px solid ' + (showSearchOptions ? '#03428d' : '#c8d4e0'),
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                        </svg>
                        البحث بواسطة
                        {selectedSearchFields.length > 0 && (
                            <span style={{
                                backgroundColor: showSearchOptions ? '#fff' : '#03428d',
                                color: showSearchOptions ? '#03428d' : '#fff',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: '700'
                            }}>
                                {selectedSearchFields.length}
                            </span>
                        )}
                    </button>

                    {/* Search Button */}
                    <button
                        type="button"
                        onClick={(e) => handleSearch(e)}
                        style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: '#03428d',
                            color: '#fff',
                            border: '1.5px solid #03428d',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease',
                            minWidth: '120px',
                            justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#023670';
                            e.currentTarget.style.borderColor = '#023670';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#03428d';
                            e.currentTarget.style.borderColor = '#03428d';
                        }}
                    >
                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                        بحث
                    </button>

                    {/* Clear Button */}
                    <button
                        type="button"
                        onClick={handleClearSearch}
                        style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: '#f7f9fb',
                            color: '#7a8a9e',
                            border: '1.5px solid #d5dce4',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease',
                            minWidth: '120px',
                            justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#edf1f5';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f7f9fb';
                        }}
                    >
                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        مسح
                    </button>

                    {/* Active filters summary */}
                    {selectedSearchFields.length > 0 && !showSearchOptions && (
                        <div style={{
                            display: 'flex',
                            gap: '0.5rem',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            flex: 1,
                            minWidth: '200px'
                        }}>
                            {selectedSearchFields.map(fieldKey => {
                                const field = searchFields.find(f => f.key === fieldKey);
                                const val = fieldSearchValues[fieldKey] || '';
                                return (
                                    <span key={fieldKey} style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.35rem',
                                        padding: '0.3rem 0.7rem',
                                        backgroundColor: val ? '#e8f4fd' : '#fef3cd',
                                        color: val ? '#03428d' : '#856404',
                                        borderRadius: '16px',
                                        fontSize: '0.8rem',
                                        fontWeight: '500',
                                        border: val ? '1px solid #bee5eb' : '1px solid #ffeeba'
                                    }}>
                                        {field?.label}{val ? `: ${val}` : ''}
                                        <button
                                            onClick={() => toggleSearchField(fieldKey)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '0',
                                                fontSize: '1rem',
                                                lineHeight: '1',
                                                color: 'inherit',
                                                opacity: 0.7
                                            }}
                                        >
                                            ×
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    )}

                    {/* Quick filters on far left */}
                    <div style={{ minWidth: '190px', flex: '0 1 220px' }}>
                        <input
                            ref={quickCustomerNameRef}
                            type="text"
                            value={quickCustomerName}
                            onChange={(e) => setQuickCustomerName(e.target.value)}
                            placeholder="اسم الزبون"
                            disabled={selectedSearchFields.length > 0}
                            style={{
                                width: '100%',
                                padding: '0.75rem 1rem',
                                border: '1.5px solid #d5dce4',
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                outline: 'none',
                                color: selectedSearchFields.length > 0 ? '#a0aec0' : '#4a5568',
                                backgroundColor: selectedSearchFields.length > 0 ? '#f0f2f5' : '#fff',
                                cursor: selectedSearchFields.length > 0 ? 'not-allowed' : 'text'
                            }}
                            onFocus={(e) => {
                                activeInputRef.current = '__quick_customer__';
                                if (selectedSearchFields.length === 0) e.target.style.borderColor = '#5b8abf';
                            }}
                            onBlur={(e) => e.target.style.borderColor = '#d5dce4'}
                        />
                    </div>

                    <div style={{ minWidth: '170px', flex: '0 1 200px' }}>
                        <input
                            ref={quickStatusNameRef}
                            type="text"
                            value={quickStatusName}
                            onChange={(e) => setQuickStatusName(e.target.value)}
                            placeholder="الحالة"
                            disabled={selectedSearchFields.length > 0}
                            style={{
                                width: '100%',
                                padding: '0.75rem 1rem',
                                border: '1.5px solid #d5dce4',
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                outline: 'none',
                                color: selectedSearchFields.length > 0 ? '#a0aec0' : '#4a5568',
                                backgroundColor: selectedSearchFields.length > 0 ? '#f0f2f5' : '#fff',
                                cursor: selectedSearchFields.length > 0 ? 'not-allowed' : 'text'
                            }}
                            onFocus={(e) => {
                                activeInputRef.current = '__quick_status__';
                                if (selectedSearchFields.length === 0) e.target.style.borderColor = '#5b8abf';
                            }}
                            onBlur={(e) => e.target.style.borderColor = '#d5dce4'}
                        />
                    </div>
                </div>

                {/* Search Options Panel */}
                {showSearchOptions && (
                    <div style={{
                        marginTop: '1rem',
                        padding: '1.5rem',
                        backgroundColor: '#f7f9fb',
                        borderRadius: '8px',
                        border: '1px solid #e0e7ef'
                    }}>
                        {/* Text Search Fields with inputs */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{ marginBottom: '1rem', color: '#4a5568', fontSize: '1rem' }}>
                                حقول البحث:
                                <span style={{ fontSize: '0.85rem', fontWeight: '400', color: '#7a8a9e', marginRight: '0.5rem' }}>
                                    (اختر الحقول المطلوبة واكتب قيمة البحث لكل حقل)
                                </span>
                            </h4>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                gap: '0.75rem'
                            }}>
                                {searchFields.map(field => {
                                    const isChecked = selectedSearchFields.includes(field.key);
                                    return (
                                        <div
                                            key={field.key}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.5rem 0.75rem',
                                                backgroundColor: isChecked ? '#eaf2fa' : '#fff',
                                                borderRadius: '8px',
                                                border: isChecked ? '1.5px solid #03428d' : '1px solid #d5dce4',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => toggleSearchField(field.key)}
                                                style={{ 
                                                    width: '16px', 
                                                    height: '16px',
                                                    accentColor: '#03428d',
                                                    cursor: 'pointer',
                                                    flexShrink: 0
                                                }}
                                            />
                                            <span style={{ 
                                                fontSize: '0.88rem', 
                                                color: isChecked ? '#03428d' : '#4a5568',
                                                fontWeight: isChecked ? '600' : '400',
                                                whiteSpace: 'nowrap',
                                                minWidth: '100px',
                                                cursor: 'pointer'
                                            }}
                                                onClick={() => toggleSearchField(field.key)}
                                            >
                                                {field.label}
                                            </span>
                                            <input
                                                ref={el => { fieldInputRefs.current[field.key] = el; }}
                                                type="text"
                                                value={fieldSearchValues[field.key] || ''}
                                                onChange={(e) => {
                                                    if (!isChecked) {
                                                        setSelectedSearchFields(prev => [...prev, field.key]);
                                                    }
                                                    setFieldSearchValues(prev => ({
                                                        ...prev,
                                                        [field.key]: e.target.value
                                                    }));
                                                }}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                                                placeholder={isChecked ? `ابحث في ${field.label}...` : ''}
                                                disabled={!isChecked}
                                                style={{
                                                    flex: 1,
                                                    padding: '0.4rem 0.6rem',
                                                    border: isChecked ? '1.5px solid #b0c7e0' : '1px solid #e0e7ef',
                                                    borderRadius: '6px',
                                                    fontSize: '0.88rem',
                                                    outline: 'none',
                                                    backgroundColor: isChecked ? '#fff' : '#f5f5f5',
                                                    color: isChecked ? '#2d3748' : '#a0aec0',
                                                    transition: 'all 0.2s ease',
                                                    cursor: isChecked ? 'text' : 'not-allowed',
                                                    minWidth: '0'
                                                }}
                                                onFocus={(e) => { activeInputRef.current = field.key; if (isChecked) e.target.style.borderColor = '#03428d'; }}
                                                onBlur={(e) => { if (isChecked) e.target.style.borderColor = '#b0c7e0'; }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Date Filters */}
                        <div>
                            <h4 style={{ marginBottom: '1rem', color: '#4a5568', fontSize: '1rem' }}>فلترة بالتاريخ:</h4>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '1.5rem',
                                alignItems: 'flex-end',
                                direction: 'rtl'
                            }}>
                                {/* Expected Arrival Date */}
                                <div style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    padding: '0.75rem 1rem',
                                    backgroundColor: '#fff',
                                    borderRadius: '10px',
                                    border: '1.5px solid #e0e7ef',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
                                }}>
                                    <span style={{ 
                                        fontWeight: '600', 
                                        color: '#4a6a8a',
                                        fontSize: '0.9rem',
                                        whiteSpace: 'nowrap'
                                    }}>تاريخ الوصول المتوقع:</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', direction: 'ltr' }}>
                                        <DateInputDDMM
                                            value={expectedArrivalDateTo}
                                            onChange={(v) => setExpectedArrivalDateTo(v)}
                                        />
                                        <span style={{ color: '#7a9bbf', fontWeight: '500' }}>←</span>
                                        <DateInputDDMM
                                            value={expectedArrivalDateFrom}
                                            onChange={(v) => setExpectedArrivalDateFrom(v)}
                                        />
                                    </div>
                                </div>

                                {/* Inspection Date */}
                                <div style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    padding: '0.75rem 1rem',
                                    backgroundColor: '#fff',
                                    borderRadius: '10px',
                                    border: '1.5px solid #e0e7ef',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
                                }}>
                                    <span style={{ 
                                        fontWeight: '600', 
                                        color: '#4a6a8a',
                                        fontSize: '0.9rem',
                                        whiteSpace: 'nowrap'
                                    }}>موعد الفحص:</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', direction: 'ltr' }}>
                                        <DateInputDDMM
                                            value={inspectionDateTo}
                                            onChange={(v) => setInspectionDateTo(v)}
                                        />
                                        <span style={{ color: '#7a9bbf', fontWeight: '500' }}>←</span>
                                        <DateInputDDMM
                                            value={inspectionDateFrom}
                                            onChange={(v) => setInspectionDateFrom(v)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <div className="error-message" style={{ marginBottom: '2rem' }}>
                    <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>{error}</span>
                </div>
            )}

            {isLoading && (
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    padding: '2rem',
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    borderRadius: '8px'
                }}>
                    <div className="spinner" style={{ width: '30px', height: '30px' }}></div>
                </div>
            )}

            {!isLoading && files.length === 0 ? (
                <div className="empty-state">
                    <svg style={{ width: '90px', height: '90px', opacity: 0.3 }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                    </svg>
                    <h3>لا توجد ملفات تخليص</h3>
                    <p>قم بإضافة ملف تخليص جديد للبدء</p>
                </div>
            ) : (
                <>
                    {viewMode === 'table' ? (
                        <div className="table-container">
                            <table className="data-table" style={{ minWidth: '3700px', width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px', textAlign: 'center', whiteSpace: 'nowrap' }}>الرقم</th>
                                        <th style={{ width: '180px', minWidth: '180px', maxWidth: '180px', whiteSpace: 'nowrap', textAlign: 'center', position: 'sticky', right: 0, background: '#03428d', zIndex: 12 }}>رقم ملف التخليص</th>
                                        <th style={{ width: '200px', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', textAlign: 'center', position: 'sticky', right: '180px', background: '#03428d', zIndex: 11, boxShadow: '-6px 0 10px rgba(0,0,0,0.2)' }}>اسم الزبون</th>
                                        <th style={{ minWidth: '150px', whiteSpace: 'nowrap', textAlign: 'center' }}>وصف البضاعة</th>
                                        <th style={{ minWidth: '113px', whiteSpace: 'nowrap', textAlign: 'center' }}>ميناء الوجهة</th>
                                        <th style={{ minWidth: '113px', whiteSpace: 'nowrap', textAlign: 'center' }}>نوع الشحنة</th>
                                        <th style={{ minWidth: '150px', whiteSpace: 'nowrap', textAlign: 'center' }}>رقم الحاوية</th>
                                        <th style={{ minWidth: '113px', whiteSpace: 'nowrap', textAlign: 'center' }}>نوع الحاوية</th>
                                        <th style={{ minWidth: '135px', whiteSpace: 'nowrap', textAlign: 'center' }}>اسم شركة الشحن</th>
                                        <th style={{ minWidth: '113px', whiteSpace: 'nowrap', textAlign: 'center' }}>وصول متوقع</th>
                                        <th style={{ minWidth: '188px', whiteSpace: 'nowrap', textAlign: 'center' }}>ملاحظات يومية</th>
                                        <th style={{ minWidth: '113px', whiteSpace: 'nowrap', textAlign: 'center' }}>موعد الفحص</th>
                                        <th style={{ minWidth: '98px', whiteSpace: 'nowrap', textAlign: 'center' }}>الحالة</th>
                                        <th style={{ minWidth: '113px', whiteSpace: 'nowrap', textAlign: 'center' }}>اسم الشفير</th>
                                        <th style={{ minWidth: '113px', whiteSpace: 'nowrap', textAlign: 'center' }}>واتساب الشفير</th>
                                        <th style={{ minWidth: '113px', whiteSpace: 'nowrap', textAlign: 'center' }}>واتساب الزبون</th>
                                        <th style={{ minWidth: '98px', whiteSpace: 'nowrap', textAlign: 'center' }}>وزن البضاعة</th>
                                        <th style={{ minWidth: '90px', whiteSpace: 'nowrap', textAlign: 'center' }}>عدد الباكج</th>
                                        <th style={{ minWidth: '113px', whiteSpace: 'nowrap', textAlign: 'center' }}>المنشأ</th>
                                        <th style={{ minWidth: '135px', whiteSpace: 'nowrap', textAlign: 'center' }}>رقم البيان الجمركي</th>
                                        <th style={{ minWidth: '113px', whiteSpace: 'nowrap', textAlign: 'center' }}>اسم المخلص</th>
                                        <th style={{ minWidth: '98px', whiteSpace: 'nowrap', textAlign: 'center' }}>نوع الملف</th>
                                        <th style={{ minWidth: '135px', whiteSpace: 'nowrap', textAlign: 'center' }}>حساب تكلفة الملف</th>
                                        <th style={{ minWidth: '135px', whiteSpace: 'nowrap', textAlign: 'center' }}>تاريخ انشاء الملف</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {files.map((file, index) => {
                                        const selectedContainer = getSelectedContainer(file);
                                        return (
                                            <tr key={file.id}>
                                                <td style={{ textAlign: 'center', fontWeight: 600, color: '#6c757d', whiteSpace: 'nowrap' }}>{(currentPage - 1) * 12 + index + 1}</td>
                                                <td style={{ width: '180px', minWidth: '180px', maxWidth: '180px', whiteSpace: 'nowrap', textAlign: 'center', position: 'sticky', right: 0, zIndex: 7 }}>
                                                    <button
                                                        onClick={() => handleEditClick(file)}
                                                        style={{
                                                            background: 'transparent',
                                                            border: 'none',
                                                            padding: '0.5rem 1rem',
                                                            cursor: 'pointer',
                                                            fontWeight: 700,
                                                            color: file.file_num ? '#03428d' : '#6c757d',
                                                            fontSize: 'inherit',
                                                            textDecoration: 'underline',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.color = '#03a9b3';
                                                            e.target.style.transform = 'scale(1.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.color = file.file_num ? '#03428d' : '#6c757d';
                                                            e.target.style.transform = 'scale(1)';
                                                        }}
                                                        title="انقر لتعديل الملف"
                                                    >
                                                        {highlightText(file.file_num, 'file_num') || 'غير محدد'}
                                                    </button>
                                                </td>
                                                <td style={{ width: '200px', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', textAlign: 'center', position: 'sticky', right: '180px', zIndex: 6, boxShadow: '-6px 0 10px rgba(0,0,0,0.2)' }}>{highlightText(file.customer_name, 'customer_name') || '-'}</td>
                                                <td style={{ textAlign: 'center' }}>{highlightText(file.goods_description, 'goods_description') || '-'}</td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightText(file.destination_port_name, 'destination_port_name') || '-'}</td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightText(file.shipment_type_name, 'shipment_type_name') || '-'}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {file.containers_data && file.containers_data.length > 0 ? (
                                                        <select 
                                                            value={selectedContainers[file.id] || file.containers_data[0].id}
                                                            onChange={(e) => handleContainerChange(file.id, e.target.value)}
                                                            style={{
                                                                padding: '0.5rem',
                                                                borderRadius: '4px',
                                                                border: '1px solid #ddd',
                                                                backgroundColor: '#fff',
                                                                cursor: 'pointer',
                                                                fontSize: '0.9rem',
                                                                width: '100%',
                                                                minWidth: '150px',
                                                                textAlign: 'center',
                                                                fontFamily: 'inherit'
                                                            }}
                                                        >
                                                            {file.containers_data.map(container => (
                                                                <option key={container.id} value={container.id} style={{ fontFamily: 'inherit' }}>
                                                                    {container.number}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : '-'}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{selectedContainer ? highlightText(selectedContainer.container_type_name, 'container_type_name') : '-'}</td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightText(file.shipment_company_name, 'shipment_company_name') || '-'}</td>
                                                <td
                                                    style={{ whiteSpace: 'nowrap', textAlign: 'center', cursor: 'pointer' }}
                                                    onClick={() => setDateEditModal({ isOpen: true, file, mode: 'arrival' })}
                                                    title="انقر لتعديل تاريخ الوصول"
                                                >
                                                    <span style={{ borderBottom: '1px dashed #5b8abf', color: '#03428d' }}>{formatDate(file.expected_arrival_date)}</span>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {file.all_notes && file.all_notes.length > 0 ? (
                                                        <button
                                                            onClick={() => handleNotesClick(file)}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '0.5rem',
                                                                padding: '0.625rem 1rem',
                                                                fontSize: '0.85rem',
                                                                fontWeight: '600',
                                                                fontFamily: 'inherit',
                                                                color: '#fff',
                                                                background: 'linear-gradient(135deg, #03428d 0%, #03a9b3 100%)',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s ease',
                                                                boxShadow: '0 2px 8px rgba(3, 66, 141, 0.3)'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(3, 66, 141, 0.4)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(3, 66, 141, 0.3)';
                                                            }}
                                                        >
                                                            <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                                                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                                            </svg>
                                                            <span>عرض الملاحظات ({file.all_notes.length})</span>
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleNotesClick(file)}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '0.5rem',
                                                                padding: '0.625rem 1rem',
                                                                fontSize: '0.85rem',
                                                                fontWeight: '600',
                                                                fontFamily: 'inherit',
                                                                color: '#03428d',
                                                                backgroundColor: '#e8f0fa',
                                                                border: '1px dashed #03428d',
                                                                borderRadius: '8px',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.background = 'linear-gradient(135deg, #03428d 0%, #03a9b3 100%)';
                                                                e.currentTarget.style.color = '#fff';
                                                                e.currentTarget.style.borderStyle = 'solid';
                                                                e.currentTarget.style.borderColor = 'transparent';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.background = '#e8f0fa';
                                                                e.currentTarget.style.color = '#03428d';
                                                                e.currentTarget.style.borderStyle = 'dashed';
                                                                e.currentTarget.style.borderColor = '#03428d';
                                                            }}
                                                        >
                                                            <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                                            </svg>
                                                            <span>إضافة ملاحظة</span>
                                                        </button>
                                                    )}
                                                </td>
                                                <td
                                                    style={{ whiteSpace: 'nowrap', textAlign: 'center', cursor: 'pointer' }}
                                                    onClick={() => setDateEditModal({ isOpen: true, file, mode: 'inspection' })}
                                                    title="انقر لتعديل موعد الفحص"
                                                >
                                                    <span style={{ borderBottom: '1px dashed #5b8abf', color: '#03428d' }}>{formatDate(file.inspection_date)}</span>
                                                    <br />
                                                    <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                                                        {formatTime(file.inspection_time)}
                                                    </span>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                                                    <span 
                                                        className="status-badge"
                                                        style={{
                                                            backgroundColor: getStatusColor(file.file_status_name).bg,
                                                            color: getStatusColor(file.file_status_name).color,
                                                            position: 'relative',
                                                            display: 'inline-block'
                                                        }}
                                                    >
                                                        <select
                                                            value={file.file_status || ''}
                                                            onChange={(e) => handleStatusChange(file.id, e.target.value)}
                                                            disabled={updatingStatusFileId === file.id}
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                opacity: 0,
                                                                cursor: updatingStatusFileId === file.id ? 'wait' : 'pointer',
                                                                fontFamily: 'inherit'
                                                            }}
                                                        >
                                                            <option value="" disabled style={{ fontFamily: 'inherit' }}>اختر الحالة</option>
                                                            {fileStatuses.map(status => (
                                                                <option key={status.id} value={status.id} style={{ fontFamily: 'inherit' }}>
                                                                    {status.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {file.file_status_name || 'اختر الحالة'}
                                                        <svg style={{ width: '12px', height: '12px', marginRight: '4px', verticalAlign: 'middle' }} fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/>
                                                        </svg>
                                                    </span>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                                                    {highlightText(file.driver_name, 'driver_name') || '-'}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {file.driver_phonenum ? (
                                                        <button 
                                                            className="whatsapp-button"
                                                            onClick={() => openWhatsApp(file.driver_phonenum)}
                                                            title={`فتح واتساب: ${file.driver_phonenum}`}
                                                        >
                                                            <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 24 24">
                                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                                            </svg>
                                                            <span dir="ltr">{file.driver_phonenum}</span>
                                                        </button>
                                                    ) : '-'}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {file.customer_phone ? (
                                                        <button 
                                                            className="whatsapp-button"
                                                            onClick={() => openWhatsApp(file.customer_phone)}
                                                            title={`فتح واتساب: ${file.customer_phone}`}
                                                        >
                                                            <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 24 24">
                                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                                            </svg>
                                                            <span dir="ltr">{file.customer_phone}</span>
                                                        </button>
                                                    ) : '-'}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{selectedContainer ? highlightText(selectedContainer.cargo_weight, 'cargo_weight') : '-'}</td>
                                                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{selectedContainer ? highlightText(selectedContainer.package_count, 'package_count') : '-'}</td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightText(file.origin_country_name, 'origin_country') || '-'}</td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightText(file.custom_dec_num, 'custom_dec_num') || '-'}</td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightText(file.authority_name, 'authority_name') || '-'}</td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightText(file.file_type_name, 'file_type_name') || '-'}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {parseFloat(file.cost) > 0 ? (
                                                        <button 
                                                            className="cost-calc-button"
                                                            onClick={() => handleCostClick(file)}
                                                            title="عرض/تعديل حسبة الملف"
                                                        >
                                                            <span>{highlightText(String(Math.floor(parseFloat(file.cost))), 'cost')}</span>
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            className="cost-calc-button"
                                                            onClick={() => handleCostClick(file)}
                                                            title="حساب تكلفة الملف"
                                                        >
                                                            <span>حسبة الملف</span>
                                                        </button>
                                                    )}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{formatDateTime(file.created_at)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="cards-grid">
                            {files.map(file => (
                                <div key={file.id} className="file-card">
                                    <div className="file-card-header">
                                        <button
                                            onClick={() => handleEditClick(file)}
                                            className="file-card-number"
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: 0,
                                                cursor: 'pointer',
                                                color: 'inherit',
                                                fontSize: 'inherit',
                                                fontWeight: 'inherit',
                                                textDecoration: 'underline',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.opacity = '0.8';
                                                e.target.style.transform = 'scale(1.02)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.opacity = '1';
                                                e.target.style.transform = 'scale(1)';
                                            }}
                                            title="انقر لتعديل الملف"
                                        >
                                            رقم ملف التخليص: {file.file_num || 'غير محدد'}
                                        </button>
                                        <span
                                            className="file-card-status"
                                            style={{
                                                backgroundColor: getStatusColor(file.file_status_name).bg,
                                                color: getStatusColor(file.file_status_name).color,
                                                position: 'relative',
                                                display: 'inline-flex',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <select
                                                value={file.file_status || ''}
                                                onChange={(e) => handleStatusChange(file.id, e.target.value)}
                                                disabled={updatingStatusFileId === file.id}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    opacity: 0,
                                                    cursor: updatingStatusFileId === file.id ? 'wait' : 'pointer',
                                                    fontFamily: 'inherit'
                                                }}
                                            >
                                                <option value="" disabled style={{ fontFamily: 'inherit' }}>اختر الحالة</option>
                                                {fileStatuses.map(status => (
                                                    <option key={status.id} value={status.id} style={{ fontFamily: 'inherit' }}>
                                                        {status.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {file.file_status_name || 'اختر الحالة'}
                                            <svg style={{ width: '10px', height: '10px', marginRight: '3px' }} fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/>
                                            </svg>
                                        </span>
                                    </div>
                                    <div className="file-card-body">
                                        <div className="file-card-row">
                                            <svg className="file-card-icon" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                                            </svg>
                                            <span className="file-card-label">الزبون:</span>
                                            <span className="file-card-value">{file.customer_name || '-'}</span>
                                        </div>
                                        <div className="file-card-row">
                                            <svg className="file-card-icon" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                                                <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                                            </svg>
                                            <span className="file-card-label">وصف البضاعة:</span>
                                            <span className="file-card-value">{file.goods_description || '-'}</span>
                                        </div>
                                        <div className="file-card-row">
                                            <svg className="file-card-icon" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                            </svg>
                                            <span className="file-card-label">نوع الملف:</span>
                                            <span className="file-card-value">{file.file_type_name || '-'}</span>
                                        </div>
                                        <div className="file-card-row">
                                            <svg className="file-card-icon" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                                            </svg>
                                            <span className="file-card-label">التكلفة:</span>
                                            <span className="file-card-value">
                                                {file.cost && parseFloat(file.cost) > 0 ? Math.floor(parseFloat(file.cost)) : '-'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Pagination */}
                    {totalPages > 1 && <Pagination />}
                </>
            )}

            {/* Add File Modal */}
            <AddClearanceFileModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSuccess={handleAddSuccess}
            />

            {/* Notes Modal */}
            <NotesModal
                isOpen={notesModalOpen}
                onClose={() => setNotesModalOpen(false)}
                notes={selectedFileNotes?.notes || []}
                fileNum={selectedFileNotes?.fileNum || ''}
                fileId={selectedFileNotes?.fileId}
                customerName={selectedFileNotes?.customerName || ''}
                goodsDescription={selectedFileNotes?.goodsDescription || ''}
                containerNumbers={selectedFileNotes?.containerNumbers || []}
                onNoteAdded={() => fetchFiles(currentPage)}
            />

            {/* Cost Calculation Modals */}
            {(selectedFileCost?.modalType === 'SEA' || selectedFileCost?.modalType === 'AIR') && (
                <SeaCostModal 
                    isOpen={costModalOpen}
                    onClose={() => setCostModalOpen(false)}
                    fileId={selectedFileCost.fileId}
                    onApply={(totalCost) => {
                        // Update cost via API
                        axiosInstance.put(`/clearance-files/${selectedFileCost.fileId}/`, {
                            cost: Math.floor(totalCost || 0)
                        }).then(() => {
                            fetchFiles(currentPage); // Refresh data
                            setCostModalOpen(false);
                        }).catch(err => {
                            console.error('Error updating cost:', err);
                        });
                    }}
                />
            )}
            {selectedFileCost?.modalType === 'LAND' && (
                <LandCostModal 
                    isOpen={costModalOpen}
                    onClose={() => setCostModalOpen(false)}
                    fileId={selectedFileCost.fileId}
                    onApply={(totalCost) => {
                        // Update cost via API
                        axiosInstance.put(`/clearance-files/${selectedFileCost.fileId}/`, {
                            cost: Math.floor(totalCost || 0)
                        }).then(() => {
                            fetchFiles(currentPage); // Refresh data
                            setCostModalOpen(false);
                        }).catch(err => {
                            console.error('Error updating cost:', err);
                        });
                    }}
                />
            )}
            {selectedFileCost?.modalType === '' && costModalOpen && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999
                    }}
                    onClick={() => setCostModalOpen(false)}
                >
                    <div 
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '16px',
                            padding: '2rem',
                            maxWidth: '400px',
                            textAlign: 'center',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                        }}
                    >
                        <svg style={{ width: '64px', height: '64px', color: '#f59e0b', margin: '0 auto 1rem' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <h3 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>نوع الملف غير مدعوم</h3>
                        <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
                            حساب التكلفة متاح فقط لملفات التخليص البحري أو الجوي أو البري
                        </p>
                        <button
                            onClick={() => setCostModalOpen(false)}
                            style={{
                                padding: '0.6rem 1.5rem',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#fff',
                                backgroundColor: '#03428d',
                                border: 'none',
                                borderRadius: '10px',
                                cursor: 'pointer'
                            }}
                        >
                            إغلاق
                        </button>
                    </div>
                </div>
            )}

            {/* Edit File Modal */}
            <EditClearanceFileModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setSelectedFileForEdit(null);
                }}
                onSuccess={handleEditSuccess}
                fileData={selectedFileForEdit}
            />

            {/* WhatsApp Notification Choice Modal */}
            <NotificationChoiceModal
                isOpen={notificationChoiceModal.isOpen}
                onClose={() => setNotificationChoiceModal({ isOpen: false, notificationType: '', notificationData: null })}
                notificationType={notificationChoiceModal.notificationType}
                notificationData={notificationChoiceModal.notificationData}
                onComplete={() => setNotificationChoiceModal({ isOpen: false, notificationType: '', notificationData: null })}
            />

            {/* Export Choice Modal */}
            {exportChoiceModalOpen && (
                <div
                    onClick={() => setExportChoiceModalOpen(false)}
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        fontFamily: 'inherit'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '16px',
                            padding: '2.25rem 2rem',
                            maxWidth: '520px',
                            width: '92%',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            textAlign: 'center',
                            fontFamily: 'inherit'
                        }}
                    >
                        <div style={{
                            width: '72px',
                            height: '72px',
                            borderRadius: '50%',
                            backgroundColor: '#e6f4ea',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 1.1rem'
                        }}>
                            <svg style={{ width: '38px', height: '38px', color: '#217346' }} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <h3 style={{
                            margin: 0,
                            color: '#1e293b',
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            marginBottom: '0.6rem',
                            fontFamily: 'inherit'
                        }}>تصدير إلى Excel</h3>
                        <p style={{
                            color: '#64748b',
                            marginBottom: '1.85rem',
                            fontSize: '1.05rem',
                            fontFamily: 'inherit'
                        }}>اختر طريقة التصدير المناسبة</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                            <button
                                onClick={() => {
                                    setExportChoiceModalOpen(false);
                                    exportToExcel();
                                }}
                                style={{
                                    padding: '1rem 1.25rem',
                                    background: 'linear-gradient(135deg, #217346 0%, #2e8b57 100%)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '12px',
                                    fontWeight: 700,
                                    fontSize: '1.1rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.65rem',
                                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                    fontFamily: 'inherit'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 6px 14px rgba(33, 115, 70, 0.35)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <svg style={{ width: 22, height: 22 }} fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M3 5c0-1.657 3.134-3 7-3s7 1.343 7 3-3.134 3-7 3-7-1.343-7-3z" />
                                    <path d="M3 7.5v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c-1.668 1.066-4.157 1.667-7 1.667S4.668 8.566 3 7.5z" />
                                    <path d="M3 12.5v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c-1.668 1.066-4.157 1.667-7 1.667S4.668 13.566 3 12.5z" />
                                </svg>
                                تصدير كامل البيانات
                            </button>
                            <button
                                onClick={openExportSelectModal}
                                style={{
                                    padding: '1rem 1.25rem',
                                    backgroundColor: '#03428d',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '12px',
                                    fontWeight: 700,
                                    fontSize: '1.1rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.65rem',
                                    transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                                    fontFamily: 'inherit'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 6px 14px rgba(3, 66, 141, 0.35)';
                                    e.currentTarget.style.backgroundColor = '#023670';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.backgroundColor = '#03428d';
                                }}
                            >
                                <svg style={{ width: 22, height: 22 }} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
                                    <rect x="3" y="3" width="18" height="18" rx="3" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                تصدير بيانات محددة
                            </button>
                            <button
                                onClick={() => setExportChoiceModalOpen(false)}
                                style={{
                                    padding: '0.85rem 1.25rem',
                                    background: '#f7f9fb',
                                    color: '#7a8a9e',
                                    border: '1.5px solid #d5dce4',
                                    borderRadius: '12px',
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    transition: 'background 0.15s ease',
                                    fontFamily: 'inherit'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#edf1f5'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f7f9fb'; }}
                            >
                                إلغاء
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Select Files for Export Modal */}
            {exportSelectModalOpen && (
                <div
                    onClick={() => setExportSelectModalOpen(false)}
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: '1rem',
                        fontFamily: 'inherit'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '16px',
                            width: '100%',
                            maxWidth: '1600px',
                            height: '92vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            overflow: 'hidden',
                            fontFamily: 'inherit'
                        }}
                    >
                        {/* Header - solid dark blue, no icon, white text */}
                        <div style={{
                            padding: '1.25rem 1.75rem',
                            backgroundColor: '#03428d',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontFamily: 'inherit'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: '#fff', fontFamily: 'inherit' }}>
                                اختر الملفات المراد تصديرها
                            </h3>
                            <button
                                onClick={() => setExportSelectModalOpen(false)}
                                style={{
                                    background: 'rgba(255,255,255,0.18)',
                                    border: 'none',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '1.6rem',
                                    lineHeight: 1,
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontFamily: 'inherit'
                                }}
                                title="إغلاق"
                            >
                                ×
                            </button>
                        </div>

                        {/* Selection summary bar */}
                        <div style={{
                            padding: '0.75rem 1.75rem',
                            borderBottom: '1px solid #e8edf2',
                            backgroundColor: '#f9fafc',
                            display: 'flex',
                            gap: '0.85rem',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            fontFamily: 'inherit'
                        }}>
                            <div style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: selectedExportIds.size > 0 ? '#e8f4fd' : '#f0f2f5',
                                color: selectedExportIds.size > 0 ? '#03428d' : '#7a8a9e',
                                borderRadius: '10px',
                                fontWeight: 700,
                                fontSize: '0.98rem',
                                border: selectedExportIds.size > 0 ? '1px solid #bee5eb' : '1px solid #e0e7ef',
                                whiteSpace: 'nowrap',
                                fontFamily: 'inherit'
                            }}>
                                تم اختيار: {selectedExportIds.size}
                            </div>
                            {selectedExportIds.size > 0 && (
                                <button
                                    onClick={() => setSelectedExportIds(new Set())}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        backgroundColor: '#fff',
                                        color: '#dc3545',
                                        border: '1.5px solid #f5c2c7',
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        fontSize: '0.92rem',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        fontFamily: 'inherit'
                                    }}
                                    title="إلغاء كل الاختيارات"
                                >
                                    مسح كل الاختيارات
                                </button>
                            )}
                        </div>

                        {/* Body (scrollable): Search section + Table */}
                        <div style={{ flex: 1, overflow: 'auto', padding: '1rem 1.5rem', backgroundColor: '#f5f7fa', fontFamily: 'inherit' }}>
                            {/* Search Section - mirrors main page */}
                            <div className="search-section" style={{
                                backgroundColor: '#fff',
                                borderRadius: '12px',
                                padding: '1.25rem',
                                marginBottom: '1.25rem',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                border: '1px solid #e8edf2',
                                fontFamily: 'inherit'
                            }}>
                                <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                    {/* General Search Input */}
                                    <div style={{ flex: '1', minWidth: '230px', position: 'relative' }}>
                                        <input
                                            ref={mGeneralSearchRef}
                                            type="text"
                                            value={mSearchQuery}
                                            onChange={(e) => setMSearchQuery(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleModalSearch(e)}
                                            placeholder="ادخل نص البحث..."
                                            disabled={mSelectedSearchFields.length > 0}
                                            style={{
                                                width: '100%',
                                                padding: '0.7rem 1rem',
                                                border: '1.5px solid #d5dce4',
                                                borderRadius: '8px',
                                                fontSize: '0.98rem',
                                                outline: 'none',
                                                color: mSelectedSearchFields.length > 0 ? '#a0aec0' : '#4a5568',
                                                backgroundColor: mSelectedSearchFields.length > 0 ? '#f0f2f5' : '#fff',
                                                cursor: mSelectedSearchFields.length > 0 ? 'not-allowed' : 'text',
                                                fontFamily: 'inherit'
                                            }}
                                            onFocus={(e) => { mActiveInputRef.current = '__general__'; if (mSelectedSearchFields.length === 0) e.target.style.borderColor = '#5b8abf'; }}
                                            onBlur={(e) => e.target.style.borderColor = '#d5dce4'}
                                        />
                                    </div>

                                    {/* Search By Toggle */}
                                    <button
                                        onClick={() => setMShowSearchOptions(!mShowSearchOptions)}
                                        style={{
                                            padding: '0.7rem 1.25rem',
                                            backgroundColor: mShowSearchOptions ? '#03428d' : '#f0f4f8',
                                            color: mShowSearchOptions ? '#fff' : '#5a6f85',
                                            border: '1.5px solid ' + (mShowSearchOptions ? '#03428d' : '#c8d4e0'),
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontWeight: 600,
                                            fontSize: '0.95rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            fontFamily: 'inherit'
                                        }}
                                    >
                                        <svg style={{ width: 16, height: 16 }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                                        </svg>
                                        البحث بواسطة
                                        {mSelectedSearchFields.length > 0 && (
                                            <span style={{
                                                backgroundColor: mShowSearchOptions ? '#fff' : '#03428d',
                                                color: mShowSearchOptions ? '#03428d' : '#fff',
                                                borderRadius: '50%',
                                                width: '20px',
                                                height: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                fontFamily: 'inherit'
                                            }}>
                                                {mSelectedSearchFields.length}
                                            </span>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={(e) => handleModalSearch(e)}
                                        style={{
                                            padding: '0.7rem 1.25rem',
                                            backgroundColor: '#03428d',
                                            color: '#fff',
                                            border: '1.5px solid #03428d',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontWeight: 600,
                                            fontSize: '0.95rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            minWidth: '110px',
                                            justifyContent: 'center',
                                            fontFamily: 'inherit'
                                        }}
                                    >
                                        <svg style={{ width: 16, height: 16 }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                        </svg>
                                        بحث
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleModalClearSearch}
                                        style={{
                                            padding: '0.7rem 1.25rem',
                                            backgroundColor: '#f7f9fb',
                                            color: '#7a8a9e',
                                            border: '1.5px solid #d5dce4',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontWeight: 600,
                                            fontSize: '0.95rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            minWidth: '110px',
                                            justifyContent: 'center',
                                            fontFamily: 'inherit'
                                        }}
                                    >
                                        <svg style={{ width: 16, height: 16 }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                        مسح
                                    </button>

                                    {/* Active filters summary */}
                                    {mSelectedSearchFields.length > 0 && !mShowSearchOptions && (
                                        <div style={{
                                            display: 'flex',
                                            gap: '0.5rem',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            flex: 1,
                                            minWidth: '180px'
                                        }}>
                                            {mSelectedSearchFields.map(fieldKey => {
                                                const field = searchFields.find(f => f.key === fieldKey);
                                                const val = mFieldSearchValues[fieldKey] || '';
                                                return (
                                                    <span key={fieldKey} style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '0.35rem',
                                                        padding: '0.3rem 0.7rem',
                                                        backgroundColor: val ? '#e8f4fd' : '#fef3cd',
                                                        color: val ? '#03428d' : '#856404',
                                                        borderRadius: '16px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: 500,
                                                        border: val ? '1px solid #bee5eb' : '1px solid #ffeeba',
                                                        fontFamily: 'inherit'
                                                    }}>
                                                        {field?.label}{val ? `: ${val}` : ''}
                                                        <button
                                                            onClick={() => toggleModalSearchField(fieldKey)}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                padding: 0,
                                                                fontSize: '1rem',
                                                                lineHeight: 1,
                                                                color: 'inherit',
                                                                opacity: 0.7,
                                                                fontFamily: 'inherit'
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Quick filters: customer name + status */}
                                    <div style={{ minWidth: '180px', flex: '0 1 200px' }}>
                                        <input
                                            ref={mQuickCustomerNameRef}
                                            type="text"
                                            value={mQuickCustomerName}
                                            onChange={(e) => setMQuickCustomerName(e.target.value)}
                                            placeholder="اسم الزبون"
                                            disabled={mSelectedSearchFields.length > 0}
                                            style={{
                                                width: '100%',
                                                padding: '0.7rem 1rem',
                                                border: '1.5px solid #d5dce4',
                                                borderRadius: '8px',
                                                fontSize: '0.92rem',
                                                outline: 'none',
                                                color: mSelectedSearchFields.length > 0 ? '#a0aec0' : '#4a5568',
                                                backgroundColor: mSelectedSearchFields.length > 0 ? '#f0f2f5' : '#fff',
                                                cursor: mSelectedSearchFields.length > 0 ? 'not-allowed' : 'text',
                                                fontFamily: 'inherit'
                                            }}
                                            onFocus={(e) => { mActiveInputRef.current = '__quick_customer__'; if (mSelectedSearchFields.length === 0) e.target.style.borderColor = '#5b8abf'; }}
                                            onBlur={(e) => e.target.style.borderColor = '#d5dce4'}
                                        />
                                    </div>
                                    <div style={{ minWidth: '160px', flex: '0 1 180px' }}>
                                        <input
                                            ref={mQuickStatusNameRef}
                                            type="text"
                                            value={mQuickStatusName}
                                            onChange={(e) => setMQuickStatusName(e.target.value)}
                                            placeholder="الحالة"
                                            disabled={mSelectedSearchFields.length > 0}
                                            style={{
                                                width: '100%',
                                                padding: '0.7rem 1rem',
                                                border: '1.5px solid #d5dce4',
                                                borderRadius: '8px',
                                                fontSize: '0.92rem',
                                                outline: 'none',
                                                color: mSelectedSearchFields.length > 0 ? '#a0aec0' : '#4a5568',
                                                backgroundColor: mSelectedSearchFields.length > 0 ? '#f0f2f5' : '#fff',
                                                cursor: mSelectedSearchFields.length > 0 ? 'not-allowed' : 'text',
                                                fontFamily: 'inherit'
                                            }}
                                            onFocus={(e) => { mActiveInputRef.current = '__quick_status__'; if (mSelectedSearchFields.length === 0) e.target.style.borderColor = '#5b8abf'; }}
                                            onBlur={(e) => e.target.style.borderColor = '#d5dce4'}
                                        />
                                    </div>
                                </div>

                                {/* Search Options Panel */}
                                {mShowSearchOptions && (
                                    <div style={{
                                        marginTop: '1rem',
                                        padding: '1.25rem',
                                        backgroundColor: '#f7f9fb',
                                        borderRadius: '8px',
                                        border: '1px solid #e0e7ef',
                                        fontFamily: 'inherit'
                                    }}>
                                        <div style={{ marginBottom: '1.25rem' }}>
                                            <h4 style={{ marginBottom: '0.85rem', color: '#4a5568', fontSize: '0.98rem', fontFamily: 'inherit' }}>
                                                حقول البحث:
                                                <span style={{ fontSize: '0.83rem', fontWeight: 400, color: '#7a8a9e', marginRight: '0.5rem', fontFamily: 'inherit' }}>
                                                    (اختر الحقول واكتب قيمة البحث لكل حقل)
                                                </span>
                                            </h4>
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                                gap: '0.7rem'
                                            }}>
                                                {searchFields.map(field => {
                                                    const isChecked = mSelectedSearchFields.includes(field.key);
                                                    return (
                                                        <div
                                                            key={field.key}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.5rem',
                                                                padding: '0.45rem 0.7rem',
                                                                backgroundColor: isChecked ? '#eaf2fa' : '#fff',
                                                                borderRadius: '8px',
                                                                border: isChecked ? '1.5px solid #03428d' : '1px solid #d5dce4'
                                                            }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={() => toggleModalSearchField(field.key)}
                                                                style={{ width: 16, height: 16, accentColor: '#03428d', cursor: 'pointer', flexShrink: 0 }}
                                                            />
                                                            <span
                                                                style={{
                                                                    fontSize: '0.86rem',
                                                                    color: isChecked ? '#03428d' : '#4a5568',
                                                                    fontWeight: isChecked ? 600 : 400,
                                                                    whiteSpace: 'nowrap',
                                                                    minWidth: '90px',
                                                                    cursor: 'pointer',
                                                                    fontFamily: 'inherit'
                                                                }}
                                                                onClick={() => toggleModalSearchField(field.key)}
                                                            >
                                                                {field.label}
                                                            </span>
                                                            <input
                                                                ref={el => { mFieldInputRefs.current[field.key] = el; }}
                                                                type="text"
                                                                value={mFieldSearchValues[field.key] || ''}
                                                                onChange={(e) => {
                                                                    if (!isChecked) {
                                                                        setMSelectedSearchFields(prev => [...prev, field.key]);
                                                                    }
                                                                    setMFieldSearchValues(prev => ({ ...prev, [field.key]: e.target.value }));
                                                                }}
                                                                onKeyPress={(e) => e.key === 'Enter' && handleModalSearch(e)}
                                                                placeholder={isChecked ? `ابحث في ${field.label}...` : ''}
                                                                disabled={!isChecked}
                                                                style={{
                                                                    flex: 1,
                                                                    padding: '0.35rem 0.55rem',
                                                                    border: isChecked ? '1.5px solid #b0c7e0' : '1px solid #e0e7ef',
                                                                    borderRadius: '6px',
                                                                    fontSize: '0.85rem',
                                                                    outline: 'none',
                                                                    backgroundColor: isChecked ? '#fff' : '#f5f5f5',
                                                                    color: isChecked ? '#2d3748' : '#a0aec0',
                                                                    cursor: isChecked ? 'text' : 'not-allowed',
                                                                    minWidth: 0,
                                                                    fontFamily: 'inherit'
                                                                }}
                                                                onFocus={(e) => { mActiveInputRef.current = field.key; if (isChecked) e.target.style.borderColor = '#03428d'; }}
                                                                onBlur={(e) => { if (isChecked) e.target.style.borderColor = '#b0c7e0'; }}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Date Filters */}
                                        <div>
                                            <h4 style={{ marginBottom: '0.85rem', color: '#4a5568', fontSize: '0.98rem', fontFamily: 'inherit' }}>فلترة بالتاريخ:</h4>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'flex-end', direction: 'rtl' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.7rem',
                                                    padding: '0.65rem 0.85rem',
                                                    backgroundColor: '#fff',
                                                    borderRadius: '10px',
                                                    border: '1.5px solid #e0e7ef',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                                }}>
                                                    <span style={{ fontWeight: 600, color: '#4a6a8a', fontSize: '0.88rem', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>تاريخ الوصول المتوقع:</span>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', direction: 'ltr' }}>
                                                        <DateInputDDMM value={mExpectedArrivalDateTo} onChange={(v) => setMExpectedArrivalDateTo(v)} />
                                                        <span style={{ color: '#7a9bbf', fontWeight: 500 }}>←</span>
                                                        <DateInputDDMM value={mExpectedArrivalDateFrom} onChange={(v) => setMExpectedArrivalDateFrom(v)} />
                                                    </div>
                                                </div>

                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.7rem',
                                                    padding: '0.65rem 0.85rem',
                                                    backgroundColor: '#fff',
                                                    borderRadius: '10px',
                                                    border: '1.5px solid #e0e7ef',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                                }}>
                                                    <span style={{ fontWeight: 600, color: '#4a6a8a', fontSize: '0.88rem', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>موعد الفحص:</span>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', direction: 'ltr' }}>
                                                        <DateInputDDMM value={mInspectionDateTo} onChange={(v) => setMInspectionDateTo(v)} />
                                                        <span style={{ color: '#7a9bbf', fontWeight: 500 }}>←</span>
                                                        <DateInputDDMM value={mInspectionDateFrom} onChange={(v) => setMInspectionDateFrom(v)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Loading / Empty / Table */}
                            {isLoadingSelectExport && selectExportFiles.length === 0 ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                                    <div className="spinner" style={{ width: 36, height: 36 }}></div>
                                </div>
                            ) : selectExportFiles.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#7a8a9e', fontFamily: 'inherit', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e8edf2' }}>
                                    <svg style={{ width: 60, height: 60, opacity: 0.3, marginBottom: '0.75rem' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                    </svg>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 700, fontFamily: 'inherit' }}>لا توجد ملفات مطابقة</div>
                                </div>
                            ) : (
                                <>
                                    {isLoadingSelectExport && (
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: '0.5rem 0', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '8px', marginBottom: '0.5rem' }}>
                                            <div className="spinner" style={{ width: 24, height: 24 }}></div>
                                        </div>
                                    )}
                                    <div style={{
                                        overflowX: 'auto',
                                        overflowY: 'visible',
                                        width: '100%',
                                        backgroundColor: '#fff',
                                        borderRadius: '9px',
                                        boxShadow: '0 2px 9px rgba(0,0,0,0.08)',
                                        position: 'relative',
                                        fontFamily: 'inherit'
                                    }}>
                                        <table style={{
                                            minWidth: '3760px',
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            fontFamily: 'inherit'
                                        }}>
                                            <thead>
                                                <tr>
                                                    {[
                                                        { label: 'select', width: 60, sticky: true },
                                                        { label: 'الرقم', width: 60 },
                                                        { label: 'رقم ملف التخليص', minWidth: 180 },
                                                        { label: 'اسم الزبون', minWidth: 200 },
                                                        { label: 'وصف البضاعة', minWidth: 150 },
                                                        { label: 'ميناء الوجهة', minWidth: 113 },
                                                        { label: 'نوع الشحنة', minWidth: 113 },
                                                        { label: 'رقم الحاوية', minWidth: 150 },
                                                        { label: 'نوع الحاوية', minWidth: 113 },
                                                        { label: 'اسم شركة الشحن', minWidth: 135 },
                                                        { label: 'وصول متوقع', minWidth: 113 },
                                                        { label: 'موعد الفحص', minWidth: 113 },
                                                        { label: 'الحالة', minWidth: 98 },
                                                        { label: 'اسم الشفير', minWidth: 113 },
                                                        { label: 'واتساب الشفير', minWidth: 113 },
                                                        { label: 'واتساب الزبون', minWidth: 113 },
                                                        { label: 'وزن البضاعة', minWidth: 98 },
                                                        { label: 'عدد الباكج', minWidth: 90 },
                                                        { label: 'المنشأ', minWidth: 113 },
                                                        { label: 'رقم البيان الجمركي', minWidth: 135 },
                                                        { label: 'اسم المخلص', minWidth: 113 },
                                                        { label: 'نوع الملف', minWidth: 98 },
                                                        { label: 'تكلفة الملف', minWidth: 120 },
                                                        { label: 'تاريخ انشاء الملف', minWidth: 135 }
                                                    ].map((col, i) => (
                                                        <th key={i} style={{
                                                            width: col.width ? `${col.width}px` : undefined,
                                                            minWidth: (col.width || col.minWidth) ? `${col.width || col.minWidth}px` : undefined,
                                                            textAlign: 'center',
                                                            whiteSpace: 'nowrap',
                                                            padding: '0.85rem 0.6rem',
                                                            background: '#03428d',
                                                            color: '#fff',
                                                            fontWeight: 700,
                                                            fontSize: '0.95rem',
                                                            position: col.sticky ? 'sticky' : 'sticky',
                                                            top: 0,
                                                            right: col.sticky ? 0 : undefined,
                                                            zIndex: col.sticky ? 13 : 10,
                                                            boxShadow: col.sticky ? '-6px 0 10px rgba(0,0,0,0.2)' : undefined,
                                                            fontFamily: 'inherit'
                                                        }}>
                                                            {col.label === 'select' ? (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={allCurrentPageSelected}
                                                                    onChange={toggleSelectAllCurrentPage}
                                                                    style={{ width: 18, height: 18, accentColor: '#fff', cursor: 'pointer' }}
                                                                    title="اختيار/إلغاء كل ملفات هذه الصفحة"
                                                                />
                                                            ) : col.label}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectExportFiles.map((file, index) => {
                                                    const selectedContainer = getModalSelectedContainer(file);
                                                    const isChecked = selectedExportIds.has(file.id);
                                                    const rowBg = isChecked ? '#eaf2fa' : (index % 2 === 0 ? '#fff' : '#fafbfc');
                                                    const tdBase = {
                                                        padding: '0.7rem 0.6rem',
                                                        borderBottom: '1px solid #e8edf2',
                                                        color: '#2d3748',
                                                        fontSize: '0.92rem',
                                                        fontFamily: 'inherit'
                                                    };
                                                    return (
                                                        <tr
                                                            key={file.id}
                                                            onClick={() => toggleExportFileSelection(file.id)}
                                                            style={{
                                                                backgroundColor: rowBg,
                                                                cursor: 'pointer',
                                                                transition: 'background 0.15s ease'
                                                            }}
                                                            onMouseEnter={(e) => { if (!isChecked) e.currentTarget.style.backgroundColor = '#f2fbfb'; }}
                                                            onMouseLeave={(e) => { if (!isChecked) e.currentTarget.style.backgroundColor = (index % 2 === 0 ? '#fff' : '#fafbfc'); }}
                                                        >
                                                            <td style={{ ...tdBase, textAlign: 'center', whiteSpace: 'nowrap', position: 'sticky', right: 0, zIndex: 8, backgroundColor: rowBg, boxShadow: '-6px 0 10px rgba(0,0,0,0.12)' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={() => toggleExportFileSelection(file.id)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    style={{ width: 18, height: 18, accentColor: '#03428d', cursor: 'pointer' }}
                                                                />
                                                            </td>
                                                            <td style={{ ...tdBase, textAlign: 'center', fontWeight: 600, color: '#6c757d', whiteSpace: 'nowrap' }}>
                                                                {(selectExportPage - 1) * 12 + index + 1}
                                                            </td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 700, color: '#03428d' }}>
                                                                {highlightModalText(file.file_num, 'file_num') || 'غير محدد'}
                                                            </td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightModalText(file.customer_name, 'customer_name') || '-'}</td>
                                                            <td style={{ ...tdBase, textAlign: 'center' }}>{highlightModalText(file.goods_description, 'goods_description') || '-'}</td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightModalText(file.destination_port_name, 'destination_port_name') || '-'}</td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightModalText(file.shipment_type_name, 'shipment_type_name') || '-'}</td>
                                                            <td style={{ ...tdBase, textAlign: 'center' }}>
                                                                {file.containers_data && file.containers_data.length > 0 ? (
                                                                    <select
                                                                        value={mSelectedContainers[file.id] || file.containers_data[0].id}
                                                                        onChange={(e) => handleModalContainerChange(file.id, e.target.value)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        style={{
                                                                            padding: '0.4rem',
                                                                            borderRadius: '4px',
                                                                            border: '1px solid #ddd',
                                                                            backgroundColor: '#fff',
                                                                            cursor: 'pointer',
                                                                            fontSize: '0.88rem',
                                                                            width: '100%',
                                                                            minWidth: '140px',
                                                                            textAlign: 'center',
                                                                            fontFamily: 'inherit'
                                                                        }}
                                                                    >
                                                                        {file.containers_data.map(container => (
                                                                            <option key={container.id} value={container.id} style={{ fontFamily: 'inherit' }}>
                                                                                {container.number}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                ) : '-'}
                                                            </td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{selectedContainer ? highlightModalText(selectedContainer.container_type_name, 'container_type_name') : '-'}</td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightModalText(file.shipment_company_name, 'shipment_company_name') || '-'}</td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{formatDate(file.expected_arrival_date)}</td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>
                                                                {formatDate(file.inspection_date)}
                                                                <br />
                                                                <span style={{ fontSize: '0.82rem', color: '#6c757d' }}>{formatTime(file.inspection_time)}</span>
                                                            </td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>
                                                                {file.file_status_name ? (
                                                                    <span
                                                                        className="status-badge"
                                                                        style={{
                                                                            backgroundColor: getStatusColor(file.file_status_name).bg,
                                                                            color: getStatusColor(file.file_status_name).color,
                                                                            display: 'inline-block',
                                                                            fontFamily: 'inherit'
                                                                        }}
                                                                    >
                                                                        {file.file_status_name}
                                                                    </span>
                                                                ) : '-'}
                                                            </td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightModalText(file.driver_name, 'driver_name') || '-'}</td>
                                                            <td style={{ ...tdBase, textAlign: 'center' }}>
                                                                {file.driver_phonenum ? (
                                                                    <button
                                                                        className="whatsapp-button"
                                                                        onClick={(e) => { e.stopPropagation(); openWhatsApp(file.driver_phonenum); }}
                                                                        title={`فتح واتساب: ${file.driver_phonenum}`}
                                                                    >
                                                                        <svg style={{ width: 18, height: 18 }} fill="currentColor" viewBox="0 0 24 24">
                                                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                                                        </svg>
                                                                        <span dir="ltr">{file.driver_phonenum}</span>
                                                                    </button>
                                                                ) : '-'}
                                                            </td>
                                                            <td style={{ ...tdBase, textAlign: 'center' }}>
                                                                {file.customer_phone ? (
                                                                    <button
                                                                        className="whatsapp-button"
                                                                        onClick={(e) => { e.stopPropagation(); openWhatsApp(file.customer_phone); }}
                                                                        title={`فتح واتساب: ${file.customer_phone}`}
                                                                    >
                                                                        <svg style={{ width: 18, height: 18 }} fill="currentColor" viewBox="0 0 24 24">
                                                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                                                        </svg>
                                                                        <span dir="ltr">{file.customer_phone}</span>
                                                                    </button>
                                                                ) : '-'}
                                                            </td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{selectedContainer ? highlightModalText(selectedContainer.cargo_weight, 'cargo_weight') : '-'}</td>
                                                            <td style={{ ...tdBase, textAlign: 'center', whiteSpace: 'nowrap' }}>{selectedContainer ? highlightModalText(selectedContainer.package_count, 'package_count') : '-'}</td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightModalText(file.origin_country_name, 'origin_country') || '-'}</td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightModalText(file.custom_dec_num, 'custom_dec_num') || '-'}</td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightModalText(file.authority_name, 'authority_name') || '-'}</td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightModalText(file.file_type_name, 'file_type_name') || '-'}</td>
                                                            <td style={{ ...tdBase, textAlign: 'center' }}>
                                                                {parseFloat(file.cost) > 0
                                                                    ? <span style={{ fontWeight: 700, color: '#03428d' }}>{highlightModalText(String(Math.floor(parseFloat(file.cost))), 'cost')}</span>
                                                                    : '-'}
                                                            </td>
                                                            <td style={{ ...tdBase, whiteSpace: 'nowrap', textAlign: 'center' }}>{formatDateTime(file.created_at)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination - same look as main page */}
                                    {selectExportTotalPages > 1 && (
                                        <div className="pagination-container" style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            marginTop: '1.25rem',
                                            padding: '0.75rem',
                                            flexWrap: 'wrap',
                                            fontFamily: 'inherit'
                                        }}>
                                            <button onClick={() => handleModalPageChange(1)} disabled={selectExportPage === 1} className="pagination-btn"
                                                style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: selectExportPage === 1 ? '#f5f5f5' : '#fff', color: selectExportPage === 1 ? '#aaa' : '#03428d', cursor: selectExportPage === 1 ? 'not-allowed' : 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                                                الأولى
                                            </button>
                                            <button onClick={() => handleModalPageChange(selectExportPage - 1)} disabled={selectExportPage === 1} className="pagination-btn"
                                                style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: selectExportPage === 1 ? '#f5f5f5' : '#fff', color: selectExportPage === 1 ? '#aaa' : '#03428d', cursor: selectExportPage === 1 ? 'not-allowed' : 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                                                السابق
                                            </button>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.5rem 1rem',
                                                backgroundColor: '#03428d',
                                                color: '#fff',
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                fontFamily: 'inherit'
                                            }}>
                                                <span>صفحة {selectExportPage} من {selectExportTotalPages}</span>
                                                <span style={{ opacity: 0.8, fontSize: '0.85rem' }}>({selectExportTotalCount} نتيجة)</span>
                                            </div>
                                            <button onClick={() => handleModalPageChange(selectExportPage + 1)} disabled={selectExportPage === selectExportTotalPages} className="pagination-btn"
                                                style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: selectExportPage === selectExportTotalPages ? '#f5f5f5' : '#fff', color: selectExportPage === selectExportTotalPages ? '#aaa' : '#03428d', cursor: selectExportPage === selectExportTotalPages ? 'not-allowed' : 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                                                التالي
                                            </button>
                                            <button onClick={() => handleModalPageChange(selectExportTotalPages)} disabled={selectExportPage === selectExportTotalPages} className="pagination-btn"
                                                style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: selectExportPage === selectExportTotalPages ? '#f5f5f5' : '#fff', color: selectExportPage === selectExportTotalPages ? '#aaa' : '#03428d', cursor: selectExportPage === selectExportTotalPages ? 'not-allowed' : 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                                                الأخيرة
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Footer with action buttons */}
                        <div style={{
                            padding: '1rem 1.75rem',
                            borderTop: '1px solid #e8edf2',
                            backgroundColor: '#fff',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '0.85rem',
                            flexWrap: 'wrap',
                            fontFamily: 'inherit'
                        }}>
                            <button
                                onClick={() => setExportSelectModalOpen(false)}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: '#f7f9fb',
                                    color: '#7a8a9e',
                                    border: '1.5px solid #d5dce4',
                                    borderRadius: 10,
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    fontFamily: 'inherit'
                                }}
                            >
                                إلغاء
                            </button>
                            <button
                                onClick={exportSelectedFiles}
                                disabled={selectedExportIds.size === 0 || isExporting}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    background: (selectedExportIds.size === 0 || isExporting)
                                        ? '#cbd5e0'
                                        : 'linear-gradient(135deg, #217346 0%, #2e8b57 100%)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 10,
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    cursor: (selectedExportIds.size === 0 || isExporting) ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.55rem',
                                    fontFamily: 'inherit'
                                }}
                            >
                                <svg style={{ width: 20, height: 20 }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                تصدير المحدد ({selectedExportIds.size})
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <DateEditModal
                isOpen={dateEditModal.isOpen}
                file={dateEditModal.file}
                mode={dateEditModal.mode}
                onClose={() => setDateEditModal({ isOpen: false, file: null, mode: 'arrival' })}
                onSuccess={(changes) => {
                    fetchFiles(currentPage);
                    
                    // Use savedFile from API response for accurate notification data
                    const savedFile = changes?.savedFile;
                    if (!savedFile?.customer_phone) return;

                    const containerNumbers = savedFile.containers_data?.map(c => c.number).filter(n => n) || [];
                    const goodsDescription = savedFile.goods_description || '';

                    if (changes?.arrivalChanged) {
                        setNotificationChoiceModal({
                            isOpen: true,
                            notificationType: 'arrival-update',
                            notificationData: {
                                customer_phone: savedFile.customer_phone,
                                file_num: savedFile.file_num || '',
                                container_numbers: containerNumbers,
                                expected_arrival_date: savedFile.expected_arrival_date || '',
                                file_cost: savedFile.cost || 0,
                                goods_description: goodsDescription
                            }
                        });
                    } else if (changes?.inspectionChanged) {
                        setNotificationChoiceModal({
                            isOpen: true,
                            notificationType: 'inspection',
                            notificationData: {
                                customer_phone: savedFile.customer_phone,
                                file_num: savedFile.file_num || '',
                                container_numbers: containerNumbers,
                                inspection_date: savedFile.inspection_date || '',
                                inspection_time: savedFile.inspection_time || '',
                                goods_description: goodsDescription
                            }
                        });
                    }
                }}
            />
        </div>
    );
};

export default ClearanceFiles;
